import { createI18n, type I18n } from 'vue-i18n'
import { setDatetimeLocale } from '~/lib/datetime'
import { setTranslator } from '~/lib/translate'
import { API_URL, APP_NAME } from '~/utils/constants'
import { AUTH_MESSAGES } from '~/boot/plugins/auth-messages'

const SUPPORTED_LOCALES = ['en', 'nl'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
const DEFAULT_LOCALE: SupportedLocale = 'nl'

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as SupportedLocale)
}

let i18nInstance: I18n | null = null

const EXECUTION_MESSAGES_NL: Record<string, string> = {
  'The form could not be saved. Your changes are still here. Please try again.':
    'Het formulier kon niet worden opgeslagen. Uw wijzigingen zijn nog aanwezig. Probeer het opnieuw.',
  'Privacy verification is missing for this published report. Employer access is blocked. Contact support for a verified replacement.':
    'De privacycontrole ontbreekt voor deze gepubliceerde rapportage. Werkgeverstoegang is geblokkeerd. Neem contact op met de ondersteuning voor een geverifieerde vervanging.',
  'This older report has no verified privacy proof. Regenerate it before publishing.':
    'Voor deze oudere rapportage ontbreekt een geverifieerde privacycontrole. Genereer de rapportage opnieuw voordat u publiceert.',
  'Failed to refresh document': 'Het document kon niet worden vernieuwd.',
  'Wait for the current save or refresh to finish before approving it.':
    'Wacht tot het opslaan of vernieuwen is afgerond voordat u goedkeurt.',
  'Wait for every save or refresh to finish before approving all forms.':
    'Wacht tot alle formulieren zijn opgeslagen en vernieuwd voordat u ze goedkeurt.',
  'Reload this AI draft before approving it.':
    'Laad dit AI-concept opnieuw voordat u het goedkeurt.',
  'Reload the AI drafts before approving all forms.':
    'Laad de AI-concepten opnieuw voordat u alle formulieren goedkeurt.',
  'Reload the AI drafts before approving them.':
    'Laad de AI-concepten opnieuw voordat u ze goedkeurt.',
  'Review every flagged field before approving this draft.':
    'Controleer elk gemarkeerd veld voordat u dit concept goedkeurt.',
  'Form approved': 'Formulier goedgekeurd',
  'All forms approved': 'Alle formulieren goedgekeurd',
  'Failed to approve form': 'Het formulier kon niet worden goedgekeurd.',
  'Failed to approve forms': 'De formulieren konden niet worden goedgekeurd.',
  'AI drafts generated': 'AI-concepten gegenereerd',
  'AI generation failed': 'Het genereren met AI is mislukt.',
  'AI Assistant': 'AI-assistent',
  'Use AI': 'AI gebruiken',
  'No consult recording was found. Paste a transcript, upload audio, or add notes to generate drafts.':
    'Er is geen consultopname gevonden. Plak een transcript, upload audio of voeg notities toe om concepten te genereren.',
  'Paste consult transcript…': 'Plak het consulttranscript…',
  'Upload audio': 'Audio uploaden',
  'Try again': 'Opnieuw proberen',
  'Generate with AI': 'Genereren met AI',
  'Generating drafts from the consult…': 'Concepten genereren op basis van het consult…',
  'Previous Form': 'Vorig formulier',
  'Next Form': 'Volgend formulier',
  'Approve form': 'Formulier goedkeuren',
  'Approve all': 'Alles goedkeuren',
  'No task to execute': 'Geen taak om uit te voeren',
  'Unable to submit: missing form identifier.':
    'Indienen is niet mogelijk: het formuliernummer ontbreekt.',
  'Unable to submit: missing survey identifier.':
    'Indienen is niet mogelijk: het vragenlijstnummer ontbreekt.',
  'Draft saved': 'Concept opgeslagen',
  'Thanks for updating the form!': 'Het formulier is bijgewerkt.',
  'Thanks for completing the form!': 'Het formulier is voltooid.',
  'Saving document changes...': 'Documentwijzigingen opslaan...',
  'Task ID not found': 'Taaknummer niet gevonden',
  'Survey completed successfully': 'Vragenlijst succesvol voltooid',
  'You are viewing this task in read-only mode. You do not have permission to complete it.':
    'U kunt deze taak alleen bekijken. U heeft geen toestemming om deze te voltooien.',
  'Report generation status could not be checked':
    'De status van het genereren van de rapportage kon niet worden gecontroleerd.',
  'Report generation timed out before completing':
    'Het genereren van de rapportage duurde te lang. Laad de taak opnieuw om de status te controleren.',
  'Scribe generation status could not be checked':
    'De status van het genereren van de AI-concepten kon niet worden gecontroleerd.',
  'Scribe generation timed out before completing':
    'Het genereren van de AI-concepten duurde te lang. Laad de taak opnieuw om de status te controleren.',
  'Scribe generation failed': 'Het genereren van de AI-concepten is mislukt.',
  'Wait for report generation to finish before publishing.':
    'Wacht tot de rapportage is gegenereerd voordat u publiceert.',
  'Only the latest successful report generation can be published.':
    'Alleen de laatst succesvol gegenereerde rapportages kunnen worden gepubliceerd.',
  'No generated reports are available to publish.':
    'Er zijn geen gegenereerde rapportages om te publiceren.',
  'Report inputs changed after generation. Generate a new report set before publishing.':
    'De brongegevens zijn na het genereren gewijzigd. Genereer nieuwe rapportages voordat u publiceert.',
  'Review the Probleemanalyse advice form: select the reintegration goal and usable work capabilities, then approve the form before generating the report.':
    'Controleer het adviesdeel van de Probleemanalyse: kies het einddoel van de re-integratie en de benutbare mogelijkheden en keur het formulier goed voordat u het rapport genereert.',
  'The generated report set is only partially published and cannot be completed safely.':
    'De rapportages zijn slechts gedeeltelijk gepubliceerd. Neem contact op met de ondersteuning.',
  'Wait for generated report document processing to finish before publishing.':
    'Wacht tot de rapportdocumenten zijn verwerkt voordat u publiceert.',
  'An employer-facing report changed after privacy verification. Generate a new report set before publishing.':
    'Een werkgeversrapportage is na de privacycontrole gewijzigd. Genereer nieuwe rapportages voordat u publiceert.',
  'The generated report set does not match the latest successful generation.':
    'De rapportages komen niet overeen met de laatste geslaagde generatie. Laad de taak opnieuw en genereer nieuwe rapportages.',
  'Employer publication blocked': 'Publicatie voor de werkgever geblokkeerd',
  'These reports changed after privacy verification. Update the source inputs and regenerate before publishing.':
    'Deze rapportages zijn na de privacycontrole gewijzigd. Werk de brongegevens bij en genereer opnieuw voordat u publiceert.',
  'Not published': 'Niet gepubliceerd',
  'Reports are generated but not published yet. Open the task to publish them.':
    'Er zijn rapportages gegenereerd die nog niet zijn gepubliceerd. Open de taak om ze te publiceren.',
  'These reports are still private drafts and have not been published yet.':
    'Deze rapportages zijn nog privéconcepten en zijn nog niet gepubliceerd.',
  'No reports have been generated for this task yet.':
    'Er zijn nog geen rapportages voor deze taak gegenereerd.',
  'AI draft withheld': 'AI-concept achtergehouden',
  'The AI draft for this form was withheld by the privacy check. Complete and review this form manually.':
    'Het AI-concept voor dit formulier is door de privacycontrole achtergehouden. Vul dit formulier handmatig in en controleer het.',
}

const LOCAL_MESSAGES: Record<SupportedLocale, Record<string, string>> = {
  en: {
    ...Object.fromEntries(Object.keys(EXECUTION_MESSAGES_NL).map((key) => [key, key])),
    ready_to_conclude_title: 'Ready to conclude',
    ready_to_conclude_hint: 'All tasks are resolved. You can conclude this activity now.',
    'Next step': 'Next step',
    '{done} of {total} tasks': '{done} of {total} tasks',
    'Conclude & bill': 'Conclude & bill',
    'Invoiced to {name}': 'Invoiced to {name}',
    'Invoice level': 'Invoice level',
    Client: 'Client',
    Employer: 'Employer',
    Rate: 'Rate',
    Duration: 'Duration',
    Quantity: 'Quantity',
    Subtotal: 'Subtotal',
    VAT: 'VAT',
    Total: 'Total',
    minutes: 'minutes',
    hour: 'hour',
    'Billed hourly': 'Billed hourly',
    'Billed per piece': 'Billed per piece',
    '1 piece': '1 piece',
    'Set by contract': 'Set by contract',
    'the client': 'the client',
    "Concluding creates an invoice line for {name}. This can't be undone.":
      "Concluding creates an invoice line for {name}. This can't be undone.",
    'Billing details are unavailable for this activity.':
      'Billing details are unavailable for this activity.',
    'The billing rate for this activity is €0. Please verify the contract.':
      'The billing rate for this activity is €0. Please verify the contract.',
    'Please review the following before billing:': 'Please review the following before billing:',
    'Could not load billing details. Please try again.':
      'Could not load billing details. Please try again.',
    'Activity concluded successfully': 'Activity concluded successfully',
    'Activity Details': 'Activity Details',
    Form: 'Form',
    'Something went wrong': 'Something went wrong',
    'Report generation': 'Report generation',
    'Generate report': 'Generate report',
    'Regenerate report': 'Regenerate report',
    'Reports ready': 'Reports ready',
    'Draft reports ready': 'Draft reports ready',
    'Reports published': 'Reports published',
    'Report publication incomplete': 'Report publication incomplete',
    'Reports generated; reload required': 'Reports generated; reload required',
    'Reload reports': 'Reload reports',
    'Report generation succeeded, but the reports could not be loaded. Reload them before continuing.':
      'Report generation succeeded, but the reports could not be loaded. Reload them before continuing.',
    'Reports published, but the task could not be refreshed. Reload to see the latest state.':
      'Reports published, but the task could not be refreshed. Reload to see the latest state.',
    'Publish reports': 'Publish reports',
    'Publishing…': 'Publishing…',
    'Failed to publish reports': 'Failed to publish reports',
    'Review every flagged report and confirm the acknowledgement before publishing.':
      'Review every flagged report and confirm the acknowledgement before publishing.',
    'This report set changed or is incomplete. Reload the task and generate a new complete set before publishing.':
      'This report set changed or is incomplete. Reload the task and generate a new complete set before publishing.',
    'Publishing could not be confirmed. Reload the task before trying again to avoid publishing twice.':
      'Publishing could not be confirmed. Reload the task before trying again to avoid publishing twice.',
    'Failed to save document changes. Please try again.':
      'Failed to save document changes. Please try again.',
    'Failed to load task': 'Failed to load task',
    'Approve all mandatory forms to generate the report.':
      'Approve all mandatory forms to generate the report.',
    'Generating the report…': 'Generating the report…',
    'Loading the generated reports…': 'Loading the generated reports…',
    'The generated reports are listed below and can be edited.':
      'The generated reports are listed below and can be edited.',
    'The generated reports are private drafts. Review and edit them before publishing.':
      'The generated reports are private drafts. Review and edit them before publishing.',
    'Employer-facing AI drafts are read-only. To make corrections, update the source inputs and regenerate the reports.':
      'Employer-facing AI drafts are read-only. To make corrections, update the source inputs and regenerate the reports.',
    'The reports are published and can no longer be edited.':
      'The reports are published and can no longer be edited.',
    'This report set is only partially published. Editing and publication are blocked; reload the task and contact support.':
      'This report set is only partially published. Editing and publication are blocked; reload the task and contact support.',
    'AI review required before publishing': 'AI review required before publishing',
    'AI could not fully verify one or more fields against the consult. Check blank and generated text carefully.':
      'AI could not fully verify one or more fields against the consult. Check blank and generated text carefully.',
    'The AI could not verify this report against the consult.':
      'The AI could not verify this report against the consult.',
    'I reviewed every flagged report against the consult and accept responsibility for publishing it.':
      'I reviewed every flagged report against the consult and accept responsibility for publishing it.',
    'Publishing makes employer-safe reports available to the employer.':
      'Publishing makes employer-safe reports available to the employer.',
    'Report generated': 'Report generated',
    'Report generated, but the task could not be refreshed. Reload to see the reports.':
      'Report generated, but the task could not be refreshed. Reload to see the reports.',
    'Report generation failed': 'Report generation failed',
    'AI drafts were generated, but the task could not be refreshed. Reload to see the drafts.':
      'AI drafts were generated, but the task could not be refreshed. Reload to see the drafts.',
    Review: 'Review',
    'This form requires AI review.': 'This form requires AI review.',
    'Mark as reviewed': 'Mark as reviewed',
    'Mark all as reviewed': 'Mark all as reviewed',
    'field(s) flagged by AI for review. Verify the highlighted fields before approving.':
      'field(s) flagged by AI for review. Verify the highlighted fields before approving.',
    ai_review_generation_timed_out: 'AI review timed out. Review and complete this item manually.',
    ai_review_generation_unavailable:
      'AI form generation was unavailable. Review and complete this item manually.',
    ai_review_privacy_unavailable:
      'The privacy check could not be completed. Review this item carefully before approval.',
    ai_review_private_content_removed:
      'Potential confidential information was removed. Verify this item before approval.',
    ai_review_verification_unavailable:
      'The AI evidence check could not be completed. Review this item manually before approval.',
    ai_review_evidence_missing:
      'The generated value could not be supported by the available consult information. Review and complete this item manually.',
    ai_review_invalid_value_removed:
      'The generated value had an invalid format and was removed. Review and complete this item manually.',
    ai_review_partial_response:
      'The AI response was incomplete. Review and complete this item manually.',
    ai_review_derived_value:
      'AI derived this value from related consult information. Verify it before approval.',
    ai_review_unknown:
      'AI flagged this item for review. Compare it with the consult before approval.',
    'One or more employer-facing AI drafts were withheld because the privacy check did not pass. Those forms were not generated; complete and review them manually.':
      'One or more employer-facing AI drafts were withheld because the privacy check did not pass. Those forms were not generated; complete and review them manually.',
    'AI drafts were generated, but one or more employer-facing forms were withheld. Complete them manually.':
      'AI drafts were generated, but one or more employer-facing forms were withheld. Complete them manually.',
    'Review every AI warning on this form before approving it.':
      'Review every AI warning on this form before approving it.',
    'Review every AI warning in every draft before approving all forms.':
      'Review every AI warning in every draft before approving all forms.',
    'Review every flagged field in each draft before approval.':
      'Review every flagged field in each draft before approval.',
    'Save your corrections before approving this draft.':
      'Save your corrections before approving this draft.',
    'Save draft': 'Save draft',
    'Save your corrections here, then use the approval controls below.':
      'Save your corrections here, then use the approval controls below.',
    'Save every changed draft before approval. Wait for the latest review warnings to load after saving.':
      'Save every changed draft before approval. Wait for the latest review warnings to load after saving.',
    'Save this draft and wait for the latest review warnings before approving it.':
      'Save this draft and wait for the latest review warnings before approving it.',
    'Save every changed draft before approving all forms.':
      'Save every changed draft before approving all forms.',
    'Fill in every required field before approving this form.':
      'Fill in every required field before approving this form.',
    'Fill in every required field before approving all forms.':
      'Fill in every required field before approving all forms.',
    'Review the Probleemanalyse advice form: select the reintegration goal and usable work capabilities, then approve the form before generating the report.':
      'Review the Probleemanalyse advice form: select the reintegration goal and usable work capabilities, then approve the form before generating the report.',
    'Draft saved, but the latest review warnings could not be loaded. Reload the task before approving.':
      'Draft saved, but the latest review warnings could not be loaded. Reload the task before approving.',
    'The draft was saved, but newer changes are still unsaved. Save them before approval.':
      'The draft was saved, but newer changes are still unsaved. Save them before approval.',
    'This draft or its review warnings changed. Review the latest warnings again before approving.':
      'This draft or its review warnings changed. Review the latest warnings again before approving.',
    'Approval was rejected because the draft changed. Reload the task and review its warnings again.':
      'Approval was rejected because the draft changed. Reload the task and review its warnings again.',
    'Regenerating replaces the current reports. Any manual edits will be overwritten.':
      'Regenerating replaces the current reports. Any manual edits will be overwritten.',
    'Optional notes to steer the report…': 'Optional notes to steer the report…',
    'Medical Contacts': 'Medical Contacts',
    'General Practitioner': 'General Practitioner',
    Specialists: 'Specialists',
    Specialist: 'Specialist',
    GP: 'GP',
    'Add GP': 'Add GP',
    'Edit GP': 'Edit GP',
    'Add specialist': 'Add specialist',
    'Edit specialist': 'Edit specialist',
    'Practice name': 'Practice name',
    Specialism: 'Specialism',
    'Select specialism': 'Select specialism',
    'Enter specialism': 'Enter specialism',
    Other: 'Other',
    'AGB-code': 'AGB-code',
    'BIG-registration number': 'BIG-registration number',
    'First name': 'First name',
    'Last name': 'Last name',
    Initials: 'Initials',
    Prefix: 'Prefix',
    'House number': 'House number',
    'Postal code': 'Postal code',
    Street: 'Street',
    City: 'City',
    State: 'State',
    Country: 'Country',
    'Select Country': 'Select Country',
    Notes: 'Notes',
    Address: 'Address',
    Phone: 'Phone',
    Email: 'Email',
    Loading: 'Loading',
    Save: 'Save',
    Cancel: 'Cancel',
    Delete: 'Delete',
    Edit: 'Edit',
    'Delete medical contact': 'Delete medical contact',
    'Are you sure you want to delete this medical contact? This action cannot be undone.':
      'Are you sure you want to delete this medical contact? This action cannot be undone.',
    'Medical contact created': 'Medical contact created',
    'Medical contact updated': 'Medical contact updated',
    'Medical contact deleted': 'Medical contact deleted',
    'Failed to save medical contact': 'Failed to save medical contact',
    'Failed to delete medical contact': 'Failed to delete medical contact',
    'Failed to load medical contacts': 'Failed to load medical contacts',
    'Failed to load specialisms': 'Failed to load specialisms',
    'This field is required.': 'This field is required.',
    'The email field is required': 'The email field is required',
    'The password field is required': 'The password field is required',
    'Must be a valid email': 'Must be a valid email',
    'No general practitioner registered for this person.':
      'No general practitioner registered for this person.',
    'No specialists registered for this person.': 'No specialists registered for this person.',
    'Legal Hold': 'Legal Hold',
    'This person could not be found.': 'This person could not be found.',
    'On Time': 'On Time',
    'Due Soon': 'Due Soon',
    Overdue: 'Overdue',
    Concluded: 'Concluded',
    Language: 'Language',
    'Switch language to Dutch': 'Switch language to Dutch',
    'Switch language to English': 'Switch language to English',
    'Sign in to {brand}': 'Sign in to {brand}',
    'Use your professional account to continue.': 'Use your professional account to continue.',
    'Absence cases, appointments and medical records in one place.':
      'Absence cases, appointments and medical records in one place.',
    'The professional portal for occupational health teams.':
      'The professional portal for occupational health teams.',
    'Professional Portal': 'Professional Portal',
    'Powered by': 'Powered by',
    'Not provided': 'Not provided',
    'Select a person': 'Select a person',
    'Their profile, employments, documents and recent activity appear here.':
      'Their profile, employments, documents and recent activity appear here.',
    'Select a client': 'Select a client',
    'Their details, employers and recent activity appear here.':
      'Their details, employers and recent activity appear here.',
    'Select an employer': 'Select an employer',
    'Their details, divisions and recent activity appear here.':
      'Their details, divisions and recent activity appear here.',
    'Select a division': 'Select a division',
    'Its departments, people and recent activity appear here.':
      'Its departments, people and recent activity appear here.',
    'Select a department': 'Select a department',
    'Its people and recent activity appear here.': 'Its people and recent activity appear here.',
    'Select a document': 'Select a document',
    'Document not available': 'Document not available',
    'This document is not available.': 'This document is not available.',
    'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.':
      'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.',
    'Failed to load the document. Please try again.':
      'Failed to load the document. Please try again.',
    'A preview, its version history and signature status appear here.':
      'A preview, its version history and signature status appear here.',
    'Select a task': 'Select a task',
    'Its questionnaire, activity and status appear here.':
      'Its questionnaire, activity and status appear here.',
    'Select an activity': 'Select an activity',
    'Its appointment, tasks and billing appear here.':
      'Its appointment, tasks and billing appear here.',
    'Select a signature': 'Select a signature',
    'A preview, its role and where it is used appear here.':
      'A preview, its role and where it is used appear here.',
    'Select a ticket': 'Select a ticket',
    'The conversation, attachments and status appear here.':
      'The conversation, attachments and status appear here.',
    'Select a professional': 'Select a professional',
    'Their calendar and availability appear here.': 'Their calendar and availability appear here.',
    'No matches': 'No matches',
    'No record matches the current search or filters. Try widening them.':
      'No record matches the current search or filters. Try widening them.',
    'Nothing here yet': 'Nothing here yet',
    'Records added to this list will appear here.': 'Records added to this list will appear here.',
    search_placeholder_fields: 'Search {entity} by {fields}…',
    search_placeholder_plain: 'Search {entity}…',
    search_placeholder_default: 'Search…',
    search_scope_empty: 'Search looks at {fields}.',
    search_field_conjunction: 'or',
    search_entity_persons: 'persons',
    search_entity_clients: 'clients',
    search_entity_employers: 'employers',
    search_entity_divisions: 'divisions',
    search_entity_departments: 'departments',
    search_entity_documents: 'documents',
    search_entity_signatures: 'signatures',
    search_entity_tickets: 'tickets',
    search_entity_tasks: 'tasks',
    search_entity_professionals: 'professionals',
    search_entity_notifications: 'notifications',
    search_entity_employments: 'employments',
    search_entity_file_access: 'file access',
    search_entity_the_action_list: 'the action list',
    search_entity_timeline_activities: 'timeline activities',
    search_entity_absentees: 'absentees',
    'Days in Range (this case)': 'Days in Range (this case)',
    'Days in Range (all cases)': 'Days in Range (all cases)',
    'Case Duration (days)': 'Case Duration (days)',
    'No results match your filters': 'No results match your filters',
    'Try removing a filter or searching for something else.':
      'Try removing a filter or searching for something else.',
    'Absence view': 'Absence view',
    'Pick an appointment on the left to see its tasks.':
      'Pick an appointment on the left to see its tasks.',
    'Select an appointment': 'Select an appointment',
    total: 'total',
    search_field_name: 'name',
    search_field_email: 'email',
    search_field_phone: 'phone',
    search_field_code: 'code',
    search_field_type: 'type',
    search_field_description: 'description',
    search_field_notes: 'notes',
    search_field_subject: 'subject',
    search_field_activity_name: 'activity name',
    search_field_message: 'message',
    vs: 'vs',
    'No earlier period to compare with': 'No earlier period to compare with',
    'Two periods is a comparison, not a trend. A trend line needs at least three.':
      'Two periods is a comparison, not a trend. A trend line needs at least three.',
    'No data for this selection': 'No data for this selection',
    'Adjust the filters or widen the period to see results.':
      'Adjust the filters or widen the period to see results.',
    'Error downloading file': 'Error downloading file',
    'Error capturing chart image': 'Error capturing chart image',
    'Error loading SVG image': 'Error loading SVG image',
    'SVG element not found in chart container': 'SVG element not found in chart container',
    'Could not get canvas context': 'Could not get canvas context',
    'Age Distribution Analysis': 'Age Distribution Analysis',
    'Age Group': 'Age Group',
    Count: 'Count',
    Percentage: 'Percentage',
    'Percentage (%)': 'Percentage (%)',
    'Yearly Absence Analysis': 'Yearly Absence Analysis',
    Year: 'Year',
    'Absence Percentage': 'Absence Percentage',
    'Absence Percentage (%)': 'Absence Percentage (%)',
    Frequency: 'Frequency',
    Gender: 'Gender',
    Category: 'Category',
    'Diagnosis Category': 'Diagnosis Category',
    'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.':
      'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.',
    'This certificate is signed and can no longer be updated.':
      'This certificate is signed and can no longer be updated.',
    'Save to keep a draft and finish later, or Complete to submit this task.':
      'Save to keep a draft and finish later, or Complete to submit this task.',
    'Complete all forms to finish the task.': 'Complete all forms to finish the task.',
    'You can refresh the documents until a form is completed.':
      'You can refresh the documents until a form is completed.',
    'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.':
      'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.',
    'Update applies to this form only.': 'Update applies to this form only.',
    'Complete the form to download the certificate.':
      'Complete the form to download the certificate.',
    'This document is signed and is the final version.':
      'This document is signed and is the final version.',
    'A document is signed and is the final version.':
      'A document is signed and is the final version.',
    'You do not have permission to view this task.':
      'You do not have permission to view this task.',
    'This task can be viewed once it has been completed.':
      'This task can be viewed once it has been completed.',
    'One of the documents is signed and is the final version.':
      'One of the documents is signed and is the final version.',
    'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.',
    'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.',
    'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.':
      'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.',
    'Updating regenerates the document with the latest data. Continue?':
      'Updating regenerates the document with the latest data. Continue?',
    'Show page list': 'Show page list',
    'Hide page list': 'Hide page list',
    'Refreshing...': 'Refreshing...',
    'Refresh Document': 'Refresh Document',
    Refresh: 'Refresh',
    Download: 'Download',
    'Failed to download the certificate': 'Failed to download the certificate',
    Notifications: 'Notifications',
    new: 'new',
    Unread: 'Unread',
    More: 'More',
    'Mark all as read': 'Mark all as read',
    'All notifications marked as read': 'All notifications marked as read',
    'View details': 'View details',
    'Back to notifications': 'Back to notifications',
    'No notifications yet': 'No notifications yet',
    'Notification not found': 'Notification not found',
    'It is not in the loaded list. Go back and choose another.':
      'It is not in the loaded list. Go back and choose another.',
    'The notifications could not be loaded.': 'The notifications could not be loaded.',
    'View all notifications': 'View all notifications',
    Today: 'Today',
    Yesterday: 'Yesterday',
    Undated: 'Undated',
    Absence: 'Absence',
    Task: 'Task',
    Deadline: 'Deadline',
    Signaling: 'Signaling',
    Notification: 'Notification',
    'Nothing unread': 'Nothing unread',
    'Everything here has been read.': 'Everything here has been read.',
    'Updates about your cases and tasks will appear here.':
      'Updates about your cases and tasks will appear here.',
    'No notifications match your search.': 'No notifications match your search.',
    'Select a notification': 'Select a notification',
    'Its full message and the record it points to appear here.':
      'Its full message and the record it points to appear here.',
    'Open related record': 'Open related record',
    'This notification has no further details.': 'This notification has no further details.',
    Open: 'Open',
    'In Progress': 'In Progress',
    Pending: 'Pending',
    Resolved: 'Resolved',
    Closed: 'Closed',
    '{task} is carried out by {professional} under task delegation from {ohp}.':
      '{task} is carried out by {professional} under task delegation from {ohp}.',
    '{task} is carried out by {professional}.': '{task} is carried out by {professional}.',
    '{delegate} holds a task delegation but {reason}.':
      '{delegate} holds a task delegation but {reason}.',
    Delegated: 'Delegated',
    'OHP instead': 'OHP instead',
    'the OHP': 'the OHP',
    // Removing an appointment from the calendar
    'The whole appointment will be removed, with every task on it.':
      'The whole appointment will be removed, with every task on it.',
    'You can undo this straight afterwards.': 'You can undo this straight afterwards.',
    'Each occurrence is removed as a whole appointment.':
      'Each occurrence is removed as a whole appointment.',
    Undo: 'Undo',
    'The appointment has been deleted.': 'The appointment has been deleted.',
    'The appointments have been deleted.': 'The appointments have been deleted.',
    'Appointment restored.': 'Appointment restored.',
    'Appointments restored.': 'Appointments restored.',
    'This appointment was changed elsewhere. The calendar has been reloaded — please open it again.':
      'This appointment was changed elsewhere. The calendar has been reloaded — please open it again.',
    'This appointment is being edited by someone else. Please try again shortly.':
      'This appointment is being edited by someone else. Please try again shortly.',
    'A repeating appointment needs either a number of occurrences or an end date.':
      'A repeating appointment needs either a number of occurrences or an end date.',
    'This appointment is being edited by someone else.':
      'This appointment is being edited by someone else.',
    'Delete appointment and activity list': 'Delete appointment and activity list',
    'Delete appointment only': 'Delete appointment only',
    'This appointment created its activity list. Delete the list with it, or keep the list and remove the appointment only.':
      'This appointment created its activity list. Delete the list with it, or keep the list and remove the appointment only.',
    'Cannot restore: the time slot is now taken.': 'Cannot restore: the time slot is now taken.',
    'Failed to restore the appointment.': 'Failed to restore the appointment.',
    // Refusing a recurring series
    'Created {n} recurring instances.': 'Created {n} recurring instances.',
    'Nothing was created. These occurrences are not bookable:':
      'Nothing was created. These occurrences are not bookable:',
    'The repeating appointment could not be created.':
      'The repeating appointment could not be created.',
    'no availability': 'no availability',
    'already booked': 'already booked',
    'and {n} more': 'and {n} more',
    'has no availability at this time': 'has no availability at this time',
    'is already booked at this time': 'is already booked at this time',
    'is not authorized for this person or organisation':
      'is not authorized for this person or organisation',
    'their task delegation is no longer valid': 'their task delegation is no longer valid',
    'is already performing another task in this slot':
      'is already performing another task in this slot',
    'cannot take it': 'cannot take it',
    'This slot no longer has a professional for every task. Please pick another slot.':
      'This slot no longer has a professional for every task. Please pick another slot.',
    'This file contains patient details. Do not import it into a personal or cloud calendar; use the calendar subscription in Settings instead.':
      'This file contains patient details. Do not import it into a personal or cloud calendar; use the calendar subscription in Settings instead.',
    'Work schedules are managed in the new professional portal.':
      'Work schedules are managed in the new professional portal.',
    "Calculated from the hours and the employer's {hours} working hours.":
      "Calculated from the hours and the employer's {hours} working hours.",
    // $t('version', count) in VersionHistoryDialog (#4998)
    version: 'version | versions',
    // RELATIONSHIP_STATUSES: EMR maps a bare 'Single' to "Enkel" (#5000)
    'Single (relationship status)': 'Single',
    'No longer available': 'No longer available',
    'No access': 'No access',
    'Could not load': 'Could not load',
    'Something went wrong. Please try again.': 'Something went wrong. Please try again.',
    Retry: 'Retry',
    'This absence no longer exists — it may have been deleted.':
      'This absence no longer exists — it may have been deleted.',
    'This employment no longer exists — it may have been deleted.':
      'This employment no longer exists — it may have been deleted.',
    'This document no longer exists — it may have been deleted.':
      'This document no longer exists — it may have been deleted.',
    'This task no longer exists — it may have been deleted.':
      'This task no longer exists — it may have been deleted.',
    'You are not authorised to view this absence.': 'You are not authorised to view this absence.',
    'You are not authorised to view this employment.':
      'You are not authorised to view this employment.',
    'You are not authorised to view this document.':
      'You are not authorised to view this document.',
    'You are not authorised to view this task.': 'You are not authorised to view this task.',
    'This person no longer exists — they may have been deleted.':
      'This person no longer exists — they may have been deleted.',
    'You are not authorised to view this person.': 'You are not authorised to view this person.',
  },
  nl: {
    ...EXECUTION_MESSAGES_NL,
    ready_to_conclude_title: 'Klaar om af te ronden',
    ready_to_conclude_hint: 'Alle taken zijn afgehandeld. U kunt deze activiteit nu afronden.',
    'Next step': 'Volgende stap',
    '{done} of {total} tasks': '{done} van {total} taken',
    'Conclude & bill': 'Afronden & factureren',
    'Invoiced to {name}': 'Gefactureerd aan {name}',
    'Invoice level': 'Factuurniveau',
    Client: 'Klant',
    Employer: 'Werkgever',
    Rate: 'Tarief',
    Duration: 'Duur',
    Quantity: 'Aantal',
    Subtotal: 'Subtotaal',
    VAT: 'Btw',
    Total: 'Totaal',
    minutes: 'minuten',
    hour: 'uur',
    'Billed hourly': 'Per uur gefactureerd',
    'Billed per piece': 'Per stuk gefactureerd',
    '1 piece': '1 stuk',
    'Set by contract': 'Vastgelegd in contract',
    'the client': 'de klant',
    "Concluding creates an invoice line for {name}. This can't be undone.":
      'Afronden maakt een factuurregel aan voor {name}. Dit kan niet ongedaan worden gemaakt.',
    'Billing details are unavailable for this activity.':
      'Factuurgegevens zijn niet beschikbaar voor deze activiteit.',
    'The billing rate for this activity is €0. Please verify the contract.':
      'Het factuurtarief voor deze activiteit is €0. Controleer het contract.',
    'Please review the following before billing:': 'Controleer het volgende voordat u factureert:',
    'Could not load billing details. Please try again.':
      'Kon de factuurgegevens niet laden. Probeer het opnieuw.',
    'Activity concluded successfully': 'Activiteit succesvol afgerond',
    'Activity Details': 'Activiteitsdetails',
    Form: 'Formulier',
    'Something went wrong': 'Er is iets misgegaan',
    'Report generation': 'Rapportgeneratie',
    'Generate report': 'Rapport genereren',
    'Regenerate report': 'Rapport opnieuw genereren',
    'Reports ready': 'Rapporten gereed',
    'Draft reports ready': 'Conceptrapporten gereed',
    'Reports published': 'Rapporten gepubliceerd',
    'Report publication incomplete': 'Rapportpublicatie onvolledig',
    'Reports generated; reload required': 'Rapporten gegenereerd; herladen vereist',
    'Reload reports': 'Rapporten herladen',
    'Report generation succeeded, but the reports could not be loaded. Reload them before continuing.':
      'Het rapport is gegenereerd, maar de rapporten konden niet worden geladen. Herlaad ze voordat u doorgaat.',
    'Reports published, but the task could not be refreshed. Reload to see the latest state.':
      'De rapporten zijn gepubliceerd, maar de taak kon niet worden vernieuwd. Herlaad om de nieuwste status te zien.',
    'Publish reports': 'Rapporten publiceren',
    'Publishing…': 'Bezig met publiceren…',
    'Failed to publish reports': 'Rapporten publiceren is mislukt',
    'Review every flagged report and confirm the acknowledgement before publishing.':
      'Controleer elk gemarkeerd rapport en bevestig de verklaring vóór publicatie.',
    'This report set changed or is incomplete. Reload the task and generate a new complete set before publishing.':
      'Deze rapportset is gewijzigd of onvolledig. Herlaad de taak en genereer een nieuwe volledige set vóór publicatie.',
    'Publishing could not be confirmed. Reload the task before trying again to avoid publishing twice.':
      'Publicatie kon niet worden bevestigd. Herlaad de taak voordat u het opnieuw probeert om dubbele publicatie te voorkomen.',
    'Failed to save document changes. Please try again.':
      'Opslaan van documentwijzigingen mislukt. Probeer het opnieuw.',
    'Failed to load task': 'Taak laden is mislukt',
    'Approve all mandatory forms to generate the report.':
      'Keur alle verplichte formulieren goed om het rapport te genereren.',
    'Generating the report…': 'Rapport wordt gegenereerd…',
    'Loading the generated reports…': 'Gegenereerde rapportages laden…',
    'The generated reports are listed below and can be edited.':
      'De gegenereerde rapporten staan hieronder en kunnen worden bewerkt.',
    'The generated reports are private drafts. Review and edit them before publishing.':
      'De gegenereerde rapporten zijn privéconcepten. Controleer en bewerk ze vóór publicatie.',
    'Employer-facing AI drafts are read-only. To make corrections, update the source inputs and regenerate the reports.':
      'AI-conceptrapporten voor de werkgever zijn alleen-lezen. Werk de brongegevens bij en genereer de rapporten opnieuw om correcties aan te brengen.',
    'The reports are published and can no longer be edited.':
      'De rapporten zijn gepubliceerd en kunnen niet meer worden bewerkt.',
    'This report set is only partially published. Editing and publication are blocked; reload the task and contact support.':
      'Deze rapportset is slechts gedeeltelijk gepubliceerd. Bewerken en publiceren zijn geblokkeerd; herlaad de taak en neem contact op met ondersteuning.',
    'AI review required before publishing': 'AI-controle vereist vóór publicatie',
    'AI could not fully verify one or more fields against the consult. Check blank and generated text carefully.':
      'AI kon een of meer velden niet volledig toetsen aan het consult. Controleer lege en gegenereerde tekst zorgvuldig.',
    'The AI could not verify this report against the consult.':
      'De AI kon dit rapport niet toetsen aan het consult.',
    'I reviewed every flagged report against the consult and accept responsibility for publishing it.':
      'Ik heb elk gemarkeerd rapport aan het consult getoetst en neem verantwoordelijkheid voor publicatie.',
    'Publishing makes employer-safe reports available to the employer.':
      'Door te publiceren worden de privacy-gecontroleerde werkgeversrapporten beschikbaar voor de werkgever.',
    'Report generated': 'Rapport gegenereerd',
    'Report generated, but the task could not be refreshed. Reload to see the reports.':
      'Het rapport is gegenereerd, maar de taak kon niet worden vernieuwd. Herlaad om de rapporten te zien.',
    'Report generation failed': 'Rapportgeneratie mislukt',
    'AI drafts were generated, but the task could not be refreshed. Reload to see the drafts.':
      'De AI-concepten zijn gegenereerd, maar de taak kon niet worden vernieuwd. Herlaad om de concepten te zien.',
    Review: 'Beoordeling',
    'This form requires AI review.': 'Dit formulier moet worden gecontroleerd.',
    'Mark as reviewed': 'Markeren als gecontroleerd',
    'Mark all as reviewed': 'Alles markeren als gecontroleerd',
    'field(s) flagged by AI for review. Verify the highlighted fields before approving.':
      'veld(en) zijn door AI gemarkeerd. Controleer de gemarkeerde velden vóór goedkeuring.',
    ai_review_generation_timed_out:
      'De AI-controle duurde te lang. Controleer en vul dit onderdeel handmatig aan.',
    ai_review_generation_unavailable:
      'Het AI-formulier kon niet worden gegenereerd. Controleer en vul dit onderdeel handmatig aan.',
    ai_review_privacy_unavailable:
      'De privacycontrole kon niet worden voltooid. Controleer dit onderdeel zorgvuldig vóór goedkeuring.',
    ai_review_private_content_removed:
      'Mogelijk vertrouwelijke informatie is verwijderd. Controleer dit onderdeel vóór goedkeuring.',
    ai_review_verification_unavailable:
      'De AI-broncontrole kon niet worden voltooid. Controleer dit onderdeel handmatig vóór goedkeuring.',
    ai_review_evidence_missing:
      'De gegenereerde waarde kon niet worden onderbouwd met de beschikbare consultinformatie. Controleer en vul dit onderdeel handmatig aan.',
    ai_review_invalid_value_removed:
      'De gegenereerde waarde had een ongeldig formaat en is verwijderd. Controleer en vul dit onderdeel handmatig aan.',
    ai_review_partial_response:
      'Het AI-antwoord was onvolledig. Controleer en vul dit onderdeel handmatig aan.',
    ai_review_derived_value:
      'AI heeft deze waarde afgeleid uit gerelateerde consultinformatie. Controleer deze vóór goedkeuring.',
    ai_review_unknown:
      'AI heeft dit onderdeel gemarkeerd voor controle. Vergelijk het met het consult vóór goedkeuring.',
    'One or more employer-facing AI drafts were withheld because the privacy check did not pass. Those forms were not generated; complete and review them manually.':
      'Een of meer AI-concepten voor de werkgever zijn tegengehouden omdat de privacycontrole niet is geslaagd. Deze formulieren zijn niet gegenereerd; vul ze handmatig in en controleer ze.',
    'AI drafts were generated, but one or more employer-facing forms were withheld. Complete them manually.':
      'De AI-concepten zijn gegenereerd, maar een of meer werkgeversformulieren zijn tegengehouden. Vul deze handmatig in.',
    'Review every AI warning on this form before approving it.':
      'Controleer elke AI-waarschuwing op dit formulier voordat u het goedkeurt.',
    'Review every AI warning in every draft before approving all forms.':
      'Controleer elke AI-waarschuwing in elk concept voordat u alle formulieren goedkeurt.',
    'Review every flagged field in each draft before approval.':
      'Controleer elk gemarkeerd veld in ieder concept vóór goedkeuring.',
    'Save your corrections before approving this draft.':
      'Sla uw correcties op voordat u dit concept goedkeurt.',
    'Save draft': 'Concept opslaan',
    'Save your corrections here, then use the approval controls below.':
      'Sla uw correcties hier op en gebruik daarna de goedkeuringsknoppen hieronder.',
    'Save every changed draft before approval. Wait for the latest review warnings to load after saving.':
      'Sla elk gewijzigd concept op vóór goedkeuring. Wacht na het opslaan tot de nieuwste controlewaarschuwingen zijn geladen.',
    'Save this draft and wait for the latest review warnings before approving it.':
      'Sla dit concept op en wacht op de nieuwste controlewaarschuwingen voordat u het goedkeurt.',
    'Save every changed draft before approving all forms.':
      'Sla elk gewijzigd concept op voordat u alle formulieren goedkeurt.',
    'Fill in every required field before approving this form.':
      'Vul alle verplichte velden in voordat u dit formulier goedkeurt.',
    'Fill in every required field before approving all forms.':
      'Vul alle verplichte velden in voordat u alle formulieren goedkeurt.',
    'Draft saved, but the latest review warnings could not be loaded. Reload the task before approving.':
      'Het concept is opgeslagen, maar de nieuwste controlewaarschuwingen konden niet worden geladen. Herlaad de taak vóór goedkeuring.',
    'The draft was saved, but newer changes are still unsaved. Save them before approval.':
      'Het concept is opgeslagen, maar nieuwere wijzigingen zijn nog niet opgeslagen. Sla deze vóór goedkeuring op.',
    'This draft or its review warnings changed. Review the latest warnings again before approving.':
      'Dit concept of de controlewaarschuwingen zijn gewijzigd. Controleer de nieuwste waarschuwingen opnieuw vóór goedkeuring.',
    'Approval was rejected because the draft changed. Reload the task and review its warnings again.':
      'Goedkeuring is geweigerd omdat het concept is gewijzigd. Herlaad de taak en controleer de waarschuwingen opnieuw.',
    'Regenerating replaces the current reports. Any manual edits will be overwritten.':
      'Opnieuw genereren vervangt de huidige rapporten. Handmatige wijzigingen gaan verloren.',
    'Optional notes to steer the report…': 'Optionele notities om het rapport te sturen…',
    'Medical Contacts': 'Medische contacten',
    'General Practitioner': 'Huisarts',
    Specialists: 'Specialisten',
    Specialist: 'Specialist',
    GP: 'Huisarts',
    'Add GP': 'Huisarts toevoegen',
    'Edit GP': 'Huisarts bewerken',
    'Add specialist': 'Specialist toevoegen',
    'Edit specialist': 'Specialist bewerken',
    'Practice name': 'Praktijknaam',
    Specialism: 'Specialisme',
    'Select specialism': 'Selecteer specialisme',
    'Enter specialism': 'Voer specialisme in',
    Other: 'Overig',
    'AGB-code': 'AGB-code',
    'BIG-registration number': 'BIG-registratienummer',
    'First name': 'Voornaam',
    'Last name': 'Achternaam',
    Initials: 'Initialen',
    Prefix: 'Voorvoegsel',
    'House number': 'Huisnummer',
    'Postal code': 'Postcode',
    Street: 'Straat',
    City: 'Plaats',
    State: 'Provincie',
    Country: 'Land',
    'Select Country': 'Selecteer land',
    Notes: 'Notities',
    Address: 'Adres',
    Phone: 'Telefoon',
    Email: 'E-mail',
    Loading: 'Laden',
    Save: 'Opslaan',
    Cancel: 'Annuleren',
    Delete: 'Verwijderen',
    Edit: 'Bewerken',
    'Delete medical contact': 'Medisch contact verwijderen',
    'Are you sure you want to delete this medical contact? This action cannot be undone.':
      'Weet u zeker dat u dit medische contact wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    'Medical contact created': 'Medisch contact aangemaakt',
    'Medical contact updated': 'Medisch contact bijgewerkt',
    'Medical contact deleted': 'Medisch contact verwijderd',
    'Failed to save medical contact': 'Opslaan van medisch contact mislukt',
    'Failed to delete medical contact': 'Verwijderen van medisch contact mislukt',
    'Failed to load medical contacts': 'Laden van medische contacten mislukt',
    'Failed to load specialisms': 'Laden van specialismen mislukt',
    'This field is required.': 'Dit veld is verplicht.',
    'The email field is required': 'Het e-mailadres is verplicht.',
    'The password field is required': 'Het wachtwoord is verplicht.',
    'Must be a valid email': 'Moet een geldig e-mailadres zijn.',
    'No general practitioner registered for this person.':
      'Geen huisarts geregistreerd voor deze persoon.',
    'No specialists registered for this person.':
      'Geen specialisten geregistreerd voor deze persoon.',
    'Legal Hold': 'Juridische bewaringsplicht',
    'This person could not be found.': 'Deze persoon kon niet worden gevonden.',
    'On Time': 'Op tijd',
    'Due Soon': 'Verloopt binnenkort',
    Overdue: 'Te laat',
    Concluded: 'Afgerond',
    Language: 'Taal',
    'Switch language to Dutch': 'Schakel taal naar Nederlands',
    'Switch language to English': 'Schakel taal naar Engels',
    'Sign in to {brand}': 'Inloggen bij {brand}',
    'Use your professional account to continue.':
      'Gebruik uw professionele account om verder te gaan.',
    'Absence cases, appointments and medical records in one place.':
      'Verzuimdossiers, afspraken en medische documenten op één plek.',
    'The professional portal for occupational health teams.':
      'Het professionele portaal voor arbodienstverleners.',
    'Professional Portal': 'Portaal voor professionals',
    'Powered by': 'Mogelijk gemaakt door',
    'Not provided': 'Niet ingevuld',
    'Select a person': 'Selecteer een persoon',
    'Their profile, employments, documents and recent activity appear here.':
      'Hier verschijnen het profiel, dienstverbanden, documenten en recente activiteit.',
    'Select a client': 'Selecteer een klant',
    'Their details, employers and recent activity appear here.':
      'Hier verschijnen de gegevens, werkgevers en recente activiteit.',
    'Select an employer': 'Selecteer een werkgever',
    'Their details, divisions and recent activity appear here.':
      'Hier verschijnen de gegevens, divisies en recente activiteit.',
    'Select a division': 'Selecteer een divisie',
    'Its departments, people and recent activity appear here.':
      'Hier verschijnen de afdelingen, personen en recente activiteit.',
    'Select a department': 'Selecteer een afdeling',
    'Its people and recent activity appear here.':
      'Hier verschijnen de personen en recente activiteit.',
    'Select a document': 'Selecteer een document',
    'Document not available': 'Document niet beschikbaar',
    'This document is not available.': 'Dit document is niet beschikbaar.',
    'This document is not available. It may have been removed, or it belongs to a task that has not been completed yet.':
      'Dit document is niet beschikbaar. Het is mogelijk verwijderd of hoort bij een taak die nog niet is voltooid.',
    'Failed to load the document. Please try again.':
      'Het document kon niet worden geladen. Probeer het opnieuw.',
    'A preview, its version history and signature status appear here.':
      'Hier verschijnen een voorbeeld, de versiegeschiedenis en de handtekeningstatus.',
    'Select a task': 'Selecteer een taak',
    'Its questionnaire, activity and status appear here.':
      'Hier verschijnen de vragenlijst, activiteit en status.',
    'Select an activity': 'Selecteer een activiteit',
    'Its appointment, tasks and billing appear here.':
      'Hier verschijnen de afspraak, taken en facturatie.',
    'Select a signature': 'Selecteer een handtekening',
    'A preview, its role and where it is used appear here.':
      'Hier verschijnen een voorbeeld, de rol en waar deze wordt gebruikt.',
    'Select a ticket': 'Selecteer een ticket',
    'The conversation, attachments and status appear here.':
      'Hier verschijnen het gesprek, bijlagen en de status.',
    'Select a professional': 'Selecteer een professional',
    'Their calendar and availability appear here.':
      'Hier verschijnen de agenda en beschikbaarheid.',
    'No matches': 'Geen resultaten',
    'No record matches the current search or filters. Try widening them.':
      'Geen enkel record voldoet aan de huidige zoekopdracht of filters. Probeer ze te verbreden.',
    'Nothing here yet': 'Nog niets',
    'Records added to this list will appear here.':
      'Records die aan deze lijst worden toegevoegd verschijnen hier.',
    search_placeholder_fields: 'Zoek {entity} op {fields}…',
    search_placeholder_plain: 'Zoek {entity}…',
    search_placeholder_default: 'Zoeken…',
    search_scope_empty: 'Er wordt gezocht op {fields}.',
    search_field_conjunction: 'of',
    search_entity_persons: 'personen',
    search_entity_clients: 'klanten',
    search_entity_employers: 'werkgevers',
    search_entity_divisions: 'divisies',
    search_entity_departments: 'afdelingen',
    search_entity_documents: 'documenten',
    search_entity_signatures: 'handtekeningen',
    search_entity_tickets: 'tickets',
    search_entity_tasks: 'taken',
    search_entity_professionals: 'professionals',
    search_entity_notifications: 'meldingen',
    search_entity_employments: 'dienstverbanden',
    search_entity_file_access: 'dossiertoegang',
    search_entity_the_action_list: 'de actielijst',
    search_entity_timeline_activities: 'tijdlijnactiviteiten',
    search_entity_absentees: 'verzuimende medewerkers',
    'Days in Range (this case)': 'Dagen in periode (dit verzuimgeval)',
    'Days in Range (all cases)': 'Dagen in periode (alle verzuimgevallen)',
    'Case Duration (days)': 'Duur verzuimgeval (dagen)',
    'No results match your filters': 'Geen resultaten voor uw filters',
    'Try removing a filter or searching for something else.':
      'Verwijder een filter of zoek op iets anders.',
    'Absence view': 'Verzuimweergave',
    'Pick an appointment on the left to see its tasks.':
      'Kies links een afspraak om de taken te zien.',
    'Select an appointment': 'Selecteer een afspraak',
    total: 'totaal',
    search_field_name: 'naam',
    search_field_email: 'e-mail',
    search_field_phone: 'telefoon',
    search_field_code: 'code',
    search_field_type: 'type',
    search_field_description: 'omschrijving',
    search_field_notes: 'notities',
    search_field_subject: 'onderwerp',
    search_field_message: 'bericht',
    search_field_activity_name: 'activiteitnaam',
    vs: 'vs',
    'No earlier period to compare with': 'Geen eerdere periode om mee te vergelijken',
    'Two periods is a comparison, not a trend. A trend line needs at least three.':
      'Twee perioden is een vergelijking, geen trend. Een trendlijn heeft er minimaal drie nodig.',
    'No data for this selection': 'Geen gegevens voor deze selectie',
    'Adjust the filters or widen the period to see results.':
      'Pas de filters aan of verruim de periode om resultaten te zien.',
    'Error downloading file': 'Fout bij het downloaden van het bestand',
    'Error capturing chart image': 'Fout bij het vastleggen van de grafiekafbeelding',
    'Error loading SVG image': 'Fout bij het laden van de SVG-afbeelding',
    'SVG element not found in chart container': 'SVG-element niet gevonden in de grafiekcontainer',
    'Could not get canvas context': 'Kan de canvascontext niet ophalen',
    'Age Distribution Analysis': 'Leeftijdsverdeling',
    'Age Group': 'Leeftijdsgroep',
    Count: 'Aantal',
    Percentage: 'Percentage',
    'Percentage (%)': 'Percentage (%)',
    'Yearly Absence Analysis': 'Verzuim per jaar',
    Year: 'Jaar',
    'Absence Percentage': 'Verzuimpercentage',
    'Absence Percentage (%)': 'Verzuimpercentage (%)',
    Frequency: 'Frequentie',
    Gender: 'Geslacht',
    Category: 'Categorie',
    'Diagnosis Category': 'Diagnosecategorie',
    'This document is signed — it is the final version and can no longer be updated. It can still be signed by the other parties if required.':
      'Dit document is ondertekend — dit is de definitieve versie en kan niet meer worden bijgewerkt. Het kan indien nodig nog door de andere partijen worden ondertekend.',
    'This certificate is signed and can no longer be updated.':
      'Dit certificaat is ondertekend en kan niet meer worden bijgewerkt.',
    'Save to keep a draft and finish later, or Complete to submit this task.':
      'Opslaan om een concept te bewaren en later af te maken, of Voltooien om deze taak in te dienen.',
    'Complete all forms to finish the task.': 'Voltooi alle formulieren om de taak af te ronden.',
    'You can refresh the documents until a form is completed.':
      'U kunt de documenten vernieuwen totdat een formulier is voltooid.',
    'A form has been completed, so the documents can no longer be refreshed. Update a form to see the new values reflected in the documents.':
      'Een formulier is voltooid, dus de documenten kunnen niet meer worden vernieuwd. Werk een formulier bij om de nieuwe waarden in de documenten te zien.',
    'Update applies to this form only.': 'Bijwerken geldt alleen voor dit formulier.',
    'Complete the form to download the certificate.':
      'Voltooi het formulier om het certificaat te downloaden.',
    'This document is signed and is the final version.':
      'Dit document is ondertekend en is de definitieve versie.',
    'A document is signed and is the final version.':
      'Een document is ondertekend en is de definitieve versie.',
    'You do not have permission to view this task.':
      'U heeft geen toestemming om deze taak te bekijken.',
    'This task can be viewed once it has been completed.':
      'Deze taak kan worden bekeken zodra deze is voltooid.',
    'One of the documents is signed and is the final version.':
      'Een van de documenten is ondertekend en is de definitieve versie.',
    'The whole task is now locked — its forms and documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — de formulieren en documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'The whole task is now locked — its form and documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — het formulier en de documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'The whole task is now locked — its documents can no longer be updated. The other parties can still sign it if required.':
      'De hele taak is nu vergrendeld — de documenten kunnen niet meer worden bijgewerkt. De andere partijen kunnen indien nodig nog ondertekenen.',
    'Updating regenerates the document with the latest data. Continue?':
      'Bijwerken genereert het document opnieuw met de nieuwste gegevens. Doorgaan?',
    'Show page list': 'Paginalijst tonen',
    'Hide page list': 'Paginalijst verbergen',
    'Refreshing...': 'Vernieuwen...',
    'Refresh Document': 'Document vernieuwen',
    Refresh: 'Vernieuwen',
    Download: 'Downloaden',
    'Failed to download the certificate': 'Kon het certificaat niet downloaden.',
    Notifications: 'Meldingen',
    new: 'nieuw',
    Unread: 'Ongelezen',
    More: 'Meer',
    'Mark all as read': 'Alles als gelezen markeren',
    'All notifications marked as read': 'Alle meldingen zijn als gelezen gemarkeerd',
    'View details': 'Details bekijken',
    'Back to notifications': 'Terug naar meldingen',
    'No notifications yet': 'Nog geen meldingen',
    'Notification not found': 'Melding niet gevonden',
    'It is not in the loaded list. Go back and choose another.':
      'Deze staat niet in de geladen lijst. Ga terug en kies een andere.',
    'The notifications could not be loaded.': 'De meldingen konden niet worden geladen.',
    'View all notifications': 'Alle meldingen bekijken',
    Today: 'Vandaag',
    Yesterday: 'Gisteren',
    Undated: 'Zonder datum',
    Absence: 'Verzuim',
    Task: 'Taak',
    Deadline: 'Deadline',
    Signaling: 'Signalering',
    Notification: 'Melding',
    'Nothing unread': 'Niets ongelezen',
    'Everything here has been read.': 'Alles hier is gelezen.',
    'Updates about your cases and tasks will appear here.':
      'Updates over uw dossiers en taken verschijnen hier.',
    'No notifications match your search.': 'Geen meldingen komen overeen met uw zoekopdracht.',
    'Select a notification': 'Selecteer een melding',
    'Its full message and the record it points to appear here.':
      'Het volledige bericht en het bijbehorende record verschijnen hier.',
    'Open related record': 'Bijbehorend record openen',
    'This notification has no further details.': 'Deze melding heeft geen verdere details.',
    Open: 'Open',
    'In Progress': 'In uitvoering',
    Pending: 'In behandeling',
    Resolved: 'Opgelost',
    Closed: 'Gesloten',
    'This file contains patient details. Do not import it into a personal or cloud calendar; use the calendar subscription in Settings instead.':
      'Dit bestand bevat patiëntgegevens. Importeer het niet in een persoonlijke of cloudagenda; gebruik in plaats daarvan het agenda-abonnement in Instellingen.',
    'Employer/HR': 'Werkgever/HR',
    'Supervisor (Employer)': 'Leidinggevende (Werkgever)',
    'Absence Contact (Employer)': 'Verzuim contactpersoon (Werkgever)',
    'HSE Contact (Employer)': 'Arbo contactpersoon (Werkgever)',
    'Main Contact (Employer)': 'Hoofdcontactpersoon (Werkgever)',
    'Main Contact (Client)': 'Hoofdcontactpersoon (Opdrachtgever)',
    '{task} is carried out by {professional} under task delegation from {ohp}.':
      '{task} wordt uitgevoerd door {professional} op basis van taakdelegatie van {ohp}.',
    '{task} is carried out by {professional}.': '{task} wordt uitgevoerd door {professional}.',
    '{delegate} holds a task delegation but {reason}.':
      '{delegate} heeft een taakdelegatie maar {reason}.',
    Delegated: 'Gedelegeerd',
    'OHP instead': 'Bedrijfsarts in plaats daarvan',
    'the OHP': 'de bedrijfsarts',
    // Removing an appointment from the calendar
    'The whole appointment will be removed, with every task on it.':
      'De hele afspraak wordt verwijderd, met alle taken erop.',
    'You can undo this straight afterwards.': 'U kunt dit direct daarna ongedaan maken.',
    'Each occurrence is removed as a whole appointment.':
      'Elke herhaling wordt als een hele afspraak verwijderd.',
    Undo: 'Ongedaan maken',
    'The appointment has been deleted.': 'De afspraak is verwijderd.',
    'The appointments have been deleted.': 'De afspraken zijn verwijderd.',
    'Appointment restored.': 'Afspraak hersteld.',
    'Appointments restored.': 'Afspraken hersteld.',
    'This appointment was changed elsewhere. The calendar has been reloaded — please open it again.':
      'Deze afspraak is elders gewijzigd. De agenda is opnieuw geladen — open de afspraak opnieuw.',
    'This appointment is being edited by someone else. Please try again shortly.':
      'Deze afspraak wordt door iemand anders bewerkt. Probeer het straks opnieuw.',
    'A repeating appointment needs either a number of occurrences or an end date.':
      'Een herhalende afspraak heeft een aantal herhalingen of een einddatum nodig.',
    'This appointment is being edited by someone else.':
      'Deze afspraak wordt door iemand anders bewerkt.',
    'Delete appointment and activity list': 'Afspraak en activiteitenlijst verwijderen',
    'Delete appointment only': 'Alleen de afspraak verwijderen',
    'This appointment created its activity list. Delete the list with it, or keep the list and remove the appointment only.':
      'Deze afspraak heeft zijn activiteitenlijst aangemaakt. Verwijder de lijst mee, of behoud de lijst en verwijder alleen de afspraak.',
    'Cannot restore: the time slot is now taken.':
      'Herstellen niet mogelijk: het tijdslot is nu bezet.',
    'Failed to restore the appointment.': 'Het herstellen van de afspraak is mislukt.',
    // Refusing a recurring series
    'Created {n} recurring instances.': '{n} herhalende afspraken aangemaakt.',
    'Nothing was created. These occurrences are not bookable:':
      'Er is niets aangemaakt. Deze herhalingen zijn niet boekbaar:',
    'The repeating appointment could not be created.':
      'De herhalende afspraak kon niet worden aangemaakt.',
    'no availability': 'geen beschikbaarheid',
    'already booked': 'al geboekt',
    'and {n} more': 'en nog {n}',
    'has no availability at this time': 'heeft op dit tijdstip geen beschikbaarheid',
    'is already booked at this time': 'heeft op dit tijdstip al een afspraak',
    'is not authorized for this person or organisation':
      'is niet geautoriseerd voor deze persoon of organisatie',
    'their task delegation is no longer valid': 'de taakdelegatie is niet langer geldig',
    'is already performing another task in this slot':
      'voert al een andere taak uit in dit tijdslot',
    'cannot take it': 'kan de taak niet uitvoeren',
    'This slot no longer has a professional for every task. Please pick another slot.':
      'Niet elke taak in dit tijdslot heeft nog een professional. Kies een ander tijdslot.',
    'Work schedules are managed in the new professional portal.':
      'Werkroosters worden beheerd in het nieuwe professionalportaal.',
    "Calculated from the hours and the employer's {hours} working hours.":
      'Berekend uit de uren en de {hours} werkuren van de werkgever.',

    // Form validation (#4995)
    'Activity is required': 'Activiteit is verplicht',
    'Client is required': 'Klant is verplicht',
    'Confirmation password must match the new password':
      'Het bevestigingswachtwoord moet overeenkomen met het nieuwe wachtwoord',
    'Current password is required': 'Huidig wachtwoord is verplicht',
    'Department is required': 'Afdeling is verplicht',
    'Description is required': 'Beschrijving is verplicht',
    'Division is required': 'Divisie is verplicht',
    'Employer is required': 'Werkgever is verplicht',
    'End time field is required': 'Het veld eindtijd is verplicht',
    'Location field is required': 'Het veld locatie is verplicht',
    'Must be a valid email address': 'Moet een geldig e-mailadres zijn',
    'New password is required': 'Nieuw wachtwoord is verplicht',
    'New password must be at least 12 characters':
      'Het nieuwe wachtwoord moet minimaal 12 tekens bevatten',
    'New password must be different from your current password':
      'Het nieuwe wachtwoord moet verschillen van uw huidige wachtwoord',
    'New password must contain at least one lowercase letter':
      'Het nieuwe wachtwoord moet minimaal één kleine letter bevatten',
    'New password must contain at least one number':
      'Het nieuwe wachtwoord moet minimaal één cijfer bevatten',
    'New password must contain at least one symbol':
      'Het nieuwe wachtwoord moet minimaal één symbool bevatten',
    'New password must contain at least one uppercase letter':
      'Het nieuwe wachtwoord moet minimaal één hoofdletter bevatten',
    'Password confirmation is required': 'Wachtwoordbevestiging is verplicht',
    'Password confirmation must be at least 12 characters':
      'De wachtwoordbevestiging moet minimaal 12 tekens bevatten',
    "Passwords don't match": 'De wachtwoorden komen niet overeen',
    'Person is required': 'Persoon is verplicht',
    'Please select an availability type': 'Selecteer een beschikbaarheidstype',
    'Reply cannot be empty': 'Het antwoord mag niet leeg zijn',
    Required: 'Vereist',
    'Room field is required': 'Het veld ruimte is verplicht',
    'Start time field is required': 'Het veld starttijd is verplicht',
    'Subject is required': 'Onderwerp is verplicht',
    'Subject must be under 500 characters': 'Onderwerp mag maximaal 500 tekens bevatten',
    'The Activity field is required': 'Het veld activiteit is verplicht',
    'The Activity is required': 'De activiteit is vereist',
    'The absence comments must be less than 1000 characters':
      'De opmerkingen bij het verzuim mogen maximaal 1000 tekens bevatten',
    'The absence end date is required and it must be a valid date.':
      'De einddatum van het verzuim is verplicht en moet een geldige datum zijn.',
    'The absence percentage change date is required and it must be a valid date.':
      'De wijzigingsdatum van het verzuimpercentage is verplicht en moet een geldige datum zijn.',
    'The absence percentage must be greater than 0': 'Het verzuimpercentage moet groter dan 0 zijn',
    'The absence start date field is required': 'Het veld begindatum verzuim is verplicht',
    'The address line 1 must be less than 255 characters':
      'Adresregel 1 mag maximaal 255 tekens bevatten',
    'The address line 2 must be less than 255 characters':
      'Adresregel 2 mag maximaal 255 tekens bevatten',
    'The address number field is required': 'Het veld huisnummer is verplicht',
    'The address number must be less than 10 characters':
      'Het huisnummer mag maximaal 10 tekens bevatten',
    'The address number must be less than 20 characters':
      'Het huisnummer mag maximaal 20 tekens bevatten',
    'The business email field is required': 'Het veld zakelijk e-mailadres is verplicht',
    'The business email must be a valid email':
      'Het zakelijke e-mailadres moet een geldig e-mailadres zijn',
    'The business number must be less than 16 characters':
      'Het zakelijke nummer mag maximaal 16 tekens bevatten',
    'The business phone must be less than 16 characters':
      'Het zakelijke telefoonnummer mag maximaal 16 tekens bevatten',
    'The city field is required': 'Het veld plaats is verplicht',
    'The city must be less than 50 characters': 'De plaats mag maximaal 50 tekens bevatten',
    'The color must be less than 7 characters': 'De kleur mag maximaal 7 tekens bevatten',
    'The contact number field is required': 'Het veld contactnummer is vereist',
    'The contact number must be less than 16 characters':
      'Het contactnummer mag maximaal 16 tekens bevatten',
    'The contract type field is required': 'Het veld contracttype is vereist',
    'The contractor field is required': 'Het veld klant is verplicht',
    'The cost center must be less than 100 characters':
      'De kostenplaats mag maximaal 100 tekens bevatten',
    'The country field is required': 'Het veld land is verplicht',
    'The date of birth field is required': 'Het veld geboortedatum is verplicht',
    'The date of birth must be a valid date': 'De geboortedatum moet een geldige datum zijn',
    'The date of birth must be in the format yyyy-mm-dd':
      'De geboortedatum moet de notatie jjjj-mm-dd hebben',
    'The department field is required': 'Het veld afdeling is vereist',
    'The description field is required': 'Het veld beschrijving is vereist',
    'The description must be at least 5 characters':
      'De beschrijving moet minimaal 5 tekens bevatten',
    'The division field is required': 'Het veld divisie is vereist',
    'The email must be a valid email': 'Het e-mailadres moet geldig zijn',
    'The email must be a valid email address': 'Het e-mailadres moet een geldig e-mailadres zijn',
    'The employer field is required': 'Het veld werkgever is vereist',
    'The end date field is required': 'Het veld einddatum is verplicht',
    'The extension field is required': 'Het veld toevoeging is verplicht',
    'The extension must be less than 10 characters':
      'De toevoeging mag maximaal 10 tekens bevatten',
    'The extension must be less than 20 characters':
      'De toevoeging mag maximaal 20 tekens bevatten',
    'The external key field is required': 'Het veld externe sleutel is verplicht',
    'The external key must be less than 50 characters':
      'De externe sleutel mag maximaal 50 tekens bevatten',
    'The first name field is required': 'Het veld voornaam is verplicht',
    'The first name must be less than 50 characters': 'De voornaam mag maximaal 50 tekens bevatten',
    'The gender field is required': 'Het veld geslacht is verplicht',
    'The hours field are required': 'Het veld uren is verplicht',
    'The hours field must be a number': 'Het veld uren moet een getal zijn',
    'The house number ext must be less than 10 characters':
      'De huisnummertoevoeging mag maximaal 10 tekens bevatten',
    'The house number must be less than 10 characters':
      'Het huisnummer mag maximaal 10 tekens bevatten',
    'The initials must be less than 10 characters':
      'De voorletters mogen maximaal 10 tekens bevatten',
    'The initials must be less than 50 characters':
      'De voorletters mogen maximaal 50 tekens bevatten',
    'The insurance company field is required': 'Het veld verzekeraar is verplicht',
    'The job description must be less than 1000 characters':
      'De functieomschrijving mag maximaal 1000 tekens bevatten',
    'The job title field is required': 'Het veld functie is verplicht',
    'The last name field is required': 'Het veld achternaam is verplicht',
    'The last name must be at least 2 characters': 'De achternaam moet minimaal 2 tekens bevatten',
    'The last name must be at least 3 characters': 'De achternaam moet minimaal 3 tekens bevatten',
    'The last name must be less than 50 characters':
      'De achternaam mag maximaal 50 tekens bevatten',
    'The message must be less than 4000 characters':
      'Het bericht mag maximaal 4000 tekens bevatten',
    'The middle name must be less than 50 characters':
      'De tweede voornaam mag maximaal 50 tekens bevatten',
    'The mobile number must be less than 16 characters':
      'Het mobiele nummer mag maximaal 16 tekens bevatten',
    'The mobile number must be less than 20 characters':
      'Het mobiele nummer mag maximaal 20 tekens bevatten',
    'The mobile phone must be less than 16 characters':
      'Het mobiele telefoonnummer mag maximaal 16 tekens bevatten',
    'The new email field is required': 'Het veld nieuw e-mailadres is verplicht',
    'The notes must be less than 5000 characters':
      'De notities mogen maximaal 5000 tekens bevatten',
    'The part time percentage field is required': 'Het veld deeltijdpercentage is verplicht',
    'The password confirmation field is required': 'Het veld wachtwoordbevestiging is verplicht',
    'The password confirmation must be at least 12 characters':
      'De wachtwoordbevestiging moet minimaal 12 tekens bevatten',
    'The password must be at least 12 characters':
      'Het wachtwoord moet minimaal 12 tekens bevatten',
    'The password must contain at least one lowercase letter':
      'Het wachtwoord moet minimaal één kleine letter bevatten',
    'The password must contain at least one number':
      'Het wachtwoord moet minimaal één cijfer bevatten',
    'The password must contain at least one symbol':
      'Het wachtwoord moet minimaal één symbool bevatten',
    'The password must contain at least one uppercase letter':
      'Het wachtwoord moet minimaal één hoofdletter bevatten',
    'The percentage must be between 0 and 100': 'Het percentage moet tussen 0 en 100 liggen',
    'The phone home must be less than 16 characters':
      'Het telefoonnummer thuis mag maximaal 16 tekens bevatten',
    'The phone home must be less than 20 characters':
      'Het telefoonnummer thuis mag maximaal 20 tekens bevatten',
    'The phone private must be less than 16 characters':
      'Het privételefoonnummer mag maximaal 16 tekens bevatten',
    'The phone work must be less than 20 characters':
      'Het telefoonnummer werk mag maximaal 20 tekens bevatten',
    'The policy number must be less than 50 characters':
      'Het polisnummer mag maximaal 50 tekens bevatten',
    'The postal code must be less than 12 characters':
      'De postcode mag maximaal 12 tekens bevatten',
    'The postal code must be less than 16 characters':
      'De postcode mag maximaal 16 tekens bevatten',
    'The postal code must be less than 6 characters': 'De postcode mag maximaal 6 tekens bevatten',
    'The prefix must be less than 10 characters':
      'Het tussenvoegsel mag maximaal 10 tekens bevatten',
    'The prefix must be less than 50 characters':
      'Het tussenvoegsel mag maximaal 50 tekens bevatten',
    'The private home number must be less than 16 characters':
      'Het privénummer thuis mag maximaal 16 tekens bevatten',
    'The registration number must be less than 20 characters':
      'Het registratienummer mag maximaal 20 tekens bevatten',
    'The ssn must be 9 characters in length': 'Het BSN moet 9 tekens lang zijn',
    'The start date field is required': 'Het veld startdatum is verplicht',
    'The state must be less than 50 characters': 'De provincie mag maximaal 50 tekens bevatten',
    'The street 2 must be less than 255 characters': 'Straat 2 mag maximaal 255 tekens bevatten',
    'The street must be less than 255 characters': 'De straat mag maximaal 255 tekens bevatten',
    'The suffix must be less than 10 characters':
      'Het achtervoegsel mag maximaal 10 tekens bevatten',
    'The suffix must be less than 50 characters':
      'Het achtervoegsel mag maximaal 50 tekens bevatten',
    'The text color must be less than 7 characters': 'De tekstkleur mag maximaal 7 tekens bevatten',
    'The time spent field is required': 'Het veld bestede tijd is verplicht',
    'The time spent must be greater than 0': 'De bestede tijd moet groter dan 0 zijn',
    'The title must be less than 250 characters': 'De titel mag maximaal 250 tekens bevatten',
    'The topic must be less than 200 characters': 'Het onderwerp mag maximaal 200 tekens bevatten',
    'The value must be a number': 'De waarde moet een getal zijn',
    'Title field is required': 'Het veld titel is verplicht',
    'Work aspects are required when NCvB classification is complete':
      'Werkaspecten zijn verplicht als de NCvB-classificatie volledig is',

    // Video call (#4996)
    'Appointment concluded': 'Afspraak afgerond',
    Attendee: 'Deelnemer',
    'Auto-detect': 'Automatisch herkennen',
    'Available in {minutes} min': 'Beschikbaar over {minutes} min',
    'Back to dashboard': 'Terug naar dashboard',
    'Call is end-to-end encrypted': 'Het gesprek is end-to-end versleuteld',
    Camera: 'Camera',
    'Camera and microphone access denied. Please allow access in your browser settings.':
      'Toegang tot camera en microfoon geweigerd. Sta toegang toe in uw browserinstellingen.',
    'Camera and microphone disabled': 'Camera en microfoon uitgeschakeld',
    'Camera disabled': 'Camera uitgeschakeld',
    'Camera not available': 'Camera niet beschikbaar',
    'Camera off': 'Camera uit',
    'Camera or microphone is already in use by another application.':
      'Camera of microfoon wordt al gebruikt door een andere toepassing.',
    'Camera or microphone not found. Please check your devices.':
      'Camera of microfoon niet gevonden. Controleer uw apparaten.',
    'Check your audio and video': 'Controleer uw beeld en geluid',
    'Checking devices...': 'Apparaten controleren...',
    'Clear captions': 'Ondertiteling wissen',
    'Click the captions button to start live transcription':
      'Klik op de ondertitelingsknop om live transcriptie te starten',
    Close: 'Sluiten',
    'Conclude Appointment': 'Afspraak afronden',
    'Conclude appointment': 'Afspraak afronden',
    'Conclude appointment?': 'Afspraak afronden?',
    'Concluded at': 'Afgerond om',
    'Connected and ready': 'Verbonden en klaar',
    'Connecting...': 'Verbinding maken...',
    'Connection failed. Please check your internet connection.':
      'Verbinding mislukt. Controleer uw internetverbinding.',
    'Connection lost. Attempting to reconnect...': 'Verbinding verbroken. Opnieuw verbinden...',
    Dutch: 'Nederlands',
    'Enabling encryption...': 'Versleuteling inschakelen...',
    'Encryption error': 'Versleutelingsfout',
    'Encryption setup failed. Call may not be encrypted.':
      'Versleuteling instellen is mislukt. Het gesprek is mogelijk niet versleuteld.',
    'End-to-end encrypted': 'End-to-end versleuteld',
    'End-to-end encryption could not be enabled':
      'End-to-end-versleuteling kon niet worden ingeschakeld',
    English: 'Engels',
    'Ensure good lighting for a clear video': 'Zorg voor goede verlichting voor een helder beeld',
    Error: 'Fout',
    Excellent: 'Uitstekend',
    'Export transcript': 'Transcript exporteren',
    'Failed to access your camera or microphone.': 'Geen toegang tot uw camera of microfoon.',
    'Failed to conclude appointment': 'Afronden van de afspraak is mislukt',
    'Failed to send message': 'Bericht verzenden is mislukt',
    'Failed to share screen': 'Scherm delen is mislukt',
    'Failed to start meeting. Please try again.':
      'Starten van de vergadering is mislukt. Probeer het opnieuw.',
    'Find a quiet place for the call': 'Zoek een rustige plek voor het gesprek',
    French: 'Frans',
    Good: 'Goed',
    'In-call messages': 'Berichten tijdens het gesprek',
    Inactive: 'Inactief',
    'Invalid meeting link': 'Ongeldige vergaderlink',
    'Join call': 'Deelnemen aan gesprek',
    Leave: 'Verlaten',
    'Leave call': 'Gesprek verlaten',
    'Leave call?': 'Gesprek verlaten?',
    'Listening for speech...': 'Luisteren naar spraak...',
    Live: 'Live',
    'Live Captions': 'Live ondertiteling',
    'Loading meeting...': 'Vergadering laden...',
    Lost: 'Verbroken',
    'Make sure your camera and microphone are working before joining.':
      'Controleer of uw camera en microfoon werken voordat u deelneemt.',
    'Meeting has already concluded': 'De vergadering is al afgerond',
    'Meeting Information': 'Vergaderinformatie',
    'Meeting is not valid': 'De vergadering is niet geldig',
    Microphone: 'Microfoon',
    'Microphone muted': 'Microfoon gedempt',
    'Mute microphone': 'Microfoon dempen',
    'New message from {name}': 'Nieuw bericht van {name}',
    'No camera or microphone found. Please connect a device.':
      'Geen camera of microfoon gevonden. Sluit een apparaat aan.',
    'No messages yet': 'Nog geen berichten',
    'No transcript to export': 'Geen transcript om te exporteren',
    'Not encrypted': 'Niet versleuteld',
    'Patient may be waiting': 'Patiënt wacht mogelijk',
    'Patient muted their microphone': 'De patiënt heeft de microfoon gedempt',
    'Patient turned off camera': 'De patiënt heeft de camera uitgezet',
    Paused: 'Gepauzeerd',
    'Please allow access to camera and microphone': 'Geef toegang tot uw camera en microfoon',
    'Please check your connection and try again.':
      'Controleer uw verbinding en probeer het opnieuw.',
    'Please wait for meeting to start.': 'Wacht tot de vergadering begint.',
    Poor: 'Zwak',
    Professional: 'Professional',
    'Reconnected successfully': 'Opnieuw verbonden',
    'Rejoin Call': 'Opnieuw deelnemen',
    'Requesting camera access...': 'Toegang tot camera aanvragen...',
    Retry: 'Opnieuw proberen',
    'Select camera': 'Selecteer een camera',
    'Select language': 'Selecteer een taal',
    'Select microphone': 'Selecteer een microfoon',
    'Send a message to start the conversation': 'Stuur een bericht om de chat te starten',
    'Setting up end-to-end encryption': 'End-to-end-versleuteling instellen',
    'Share screen': 'Scherm delen',
    'Something went wrong, please try again': 'Er is iets misgegaan, probeer het opnieuw',
    'Something went wrong. Please try again.': 'Er is iets misgegaan. Probeer het opnieuw.',
    'Sound detected': 'Geluid gedetecteerd',
    Spanish: 'Spaans',
    'Speak to test': 'Zeg iets om te testen',
    'Start captions': 'Ondertiteling starten',
    'Start Meeting': 'Vergadering starten',
    'Stop captions': 'Ondertiteling stoppen',
    'Stop sharing': 'Delen stoppen',
    'Test speaker': 'Luidspreker testen',
    'The patient will appear here when they join the call.':
      'De patiënt verschijnt hier zodra deze aan het gesprek deelneemt.',
    'This call is not end-to-end encrypted': 'Dit gesprek is niet end-to-end versleuteld',
    'This call is protected with end-to-end encryption':
      'Dit gesprek is beveiligd met end-to-end-versleuteling',
    'This meeting is not valid': 'Deze vergadering is niet geldig',
    'This will end the call for both participants and conclude the appointment. This action cannot be undone.':
      'Hiermee beëindigt u het gesprek voor beide deelnemers en rondt u de afspraak af. Dit kan niet ongedaan worden gemaakt.',
    'Toggle captions': 'Ondertiteling aan- of uitzetten',
    'Toggle chat': 'Chat tonen of verbergen',
    'Transcript exported': 'Transcript geëxporteerd',
    'Transcription error: {message}': 'Transcriptiefout: {message}',
    'Try again': 'Opnieuw proberen',
    'Turn off camera': 'Camera uitzetten',
    'Turn on camera': 'Camera aanzetten',
    'Type a message...': 'Typ een bericht...',
    'Unable to join meeting. Please try again.':
      'Kan niet deelnemen aan de vergadering. Probeer het opnieuw.',
    'Unable to load meeting': 'Kan vergadering niet laden',
    Unknown: 'Onbekend',
    'Unmute microphone': 'Microfoon aanzetten',
    'Unsupported browser': 'Browser niet ondersteund',
    'Waiting for patient': 'Wachten op patiënt',
    'Waiting for professional': 'Wachten op professional',
    You: 'U',
    'You are sharing your screen': 'U deelt uw scherm',
    'You can start the meeting up to 30 minutes before the scheduled time.':
      'U kunt de vergadering tot 30 minuten vóór de geplande tijd starten.',
    'You will disconnect from the video call. The appointment will remain open and you can rejoin later.':
      'U verlaat het videogesprek. De afspraak blijft open en u kunt later opnieuw deelnemen.',
    'Your browser does not support end-to-end encryption':
      'Uw browser ondersteunt geen end-to-end-versleuteling',
    'Your professional will join the call shortly.':
      'Uw professional neemt zo dadelijk deel aan het gesprek.',
    '{name} camera off': 'Camera van {name} is uit',
    '{name} is sharing their screen': '{name} deelt het scherm',
    '{name} joined the call': '{name} is bij het gesprek gekomen',
    '{name} left the call': '{name} heeft het gesprek verlaten',
    '{name} muted': '{name} is gedempt',

    // Smart Scheduling and availability (#4997)
    'Activities from contracts': 'Activiteiten uit contracten',
    'Activities from webshop': 'Activiteiten uit de webshop',
    Activity: 'Activiteit',
    'Activity Type': 'Activiteitstype',
    Adjacency: 'Aansluiting',
    'Advance notice': 'Termijn vooraf',
    'Advanced Options': 'Geavanceerde opties',
    Afternoon: 'Middag',
    'All Day': 'Hele dag',
    'All events': 'Alle gebeurtenissen',
    'Appointment Booked!': 'Afspraak geboekt!',
    'Appointment Type': 'Afspraaktype',
    'Auto Schedule': 'Automatisch inplannen',
    'Auto scheduling failed': 'Automatisch inplannen is mislukt',
    'Availability Type': 'Type beschikbaarheid',
    'Available Slots': 'Beschikbare tijdsloten',
    Back: 'Terug',
    Background: 'Achtergrond',
    'Briefly describe the reason…': 'Beschrijf kort de reden…',
    'Choose Client': 'Kies klant',
    'Choose Department': 'Kies afdeling',
    'Choose Division': 'Kies divisie',
    'Choose Employer': 'Kies werkgever',
    'Choose Location': 'Kies locatie',
    'Choose Person': 'Kies persoon',
    'Choose the activity': 'Kies de activiteit',
    'Chosen Date': 'Gekozen datum',
    client: 'Klant',
    Confirm: 'Bevestigen',
    'Confirm Booking': 'Boeking bevestigen',
    Continue: 'Doorgaan',
    Continuity: 'Continuïteit',
    'Contract Activity': 'Contractactiviteit',
    'Create Availability': 'Beschikbaarheid aanmaken',
    'Custom Event Color': 'Eigen kleur',
    Date: 'Datum',
    'Date urgency': 'Urgentie',
    Day: 'Dag',
    'Day fill': 'Dagvulling',
    'DD-MM-YYYY': 'DD-MM-JJJJ',
    'Delete recurring event': 'Terugkerende afspraak verwijderen',
    department: 'Afdeling',
    Description: 'Beschrijving',
    division: 'Divisie',
    'Edit Availability': 'Beschikbaarheid bewerken',
    'Edit recurring events': 'Terugkerende afspraken bewerken',
    employer: 'Werkgever',
    End: 'Eind',
    'End Date': 'Einddatum',
    'Enter a description for this availability...':
      'Voer een beschrijving in voor deze beschikbaarheid...',
    Entity: 'Entiteit',
    'Entity Type': 'Entiteitstype',
    Evening: 'Avond',
    'Event Preview': 'Voorbeeld',
    'Every Day': 'Elke dag',
    'Failed to book appointment': 'Afspraak boeken is mislukt',
    'Failed to load activity locations':
      'De locaties van de activiteit konden niet worden geladen.',
    'Failed to load available slots': 'Beschikbare tijdsloten laden is mislukt',
    'Finding and booking the best slot...': 'Het beste tijdslot zoeken en boeken...',
    'Fit Score': 'Geschiktheidsscore',
    'From Date': 'Vanaf datum',
    "Helps us improve future suggestions. Skip if you'd rather not say.":
      'Dit helpt ons toekomstige suggesties te verbeteren. Sla over als u liever geen reden opgeeft.',
    'HH:MM': 'UU:MM',
    'Let the system pick the best slot automatically':
      'Laat het systeem automatisch het beste tijdslot kiezen',
    'Loading slots...': 'Tijdsloten laden...',
    'Loading...': 'Laden...',
    Location: 'Locatie',
    'Location & Room': 'Locatie en kamer',
    min: 'Min',
    Month: 'Maand',
    Morning: 'Ochtend',
    'No available slots found.': 'Geen beschikbare tijdsloten gevonden.',
    'No available slots found. Try scheduling manually.':
      'Geen beschikbare tijdsloten gevonden. Probeer handmatig in te plannen.',
    'No slots available': 'Geen tijdsloten beschikbaar',
    Organisation: 'Organisatie',
    'Organisation Level': 'Organisatieniveau',
    'Other (specify)': 'Anders, namelijk…',
    'Patient unavailable': 'Patiënt niet beschikbaar',
    Person: 'Persoon',
    'Please describe the reason.': 'Beschrijf de reden.',
    'Please fix the validation errors': 'Herstel de fouten in het formulier',
    'Please pick a reason or click Skip.': 'Kies een reden of klik op Overslaan.',
    'Preference match': 'Voorkeursmatch',
    'Preferred Time': 'Voorkeurstijd',
    Reason: 'Reden',
    Recurring: 'Terugkerend',
    'Repeat on': 'Herhalen op',
    'Restrict to Specific Clients': 'Beperken tot specifieke klanten',
    'Restricted to Clients': 'Beperkt tot klanten',
    Room: 'Kamer',
    'Schedule Another': 'Nog een afspraak inplannen',
    'Schedule Appointment': 'Afspraak plannen',
    'Schedule for a company entity': 'Inplannen voor een organisatieonderdeel',
    'Schedule for an individual': 'Inplannen voor een persoon',
    'Schedule Pattern': 'Planningspatroon',
    'Search by name...': 'Zoeken op naam...',
    'Select a reason…': 'Selecteer een reden…',
    'Select an activity...': 'Selecteer een activiteit...',
    'Select client...': 'Selecteer een klant...',
    'Select Clients': 'Klanten selecteren',
    'Select clients...': 'Selecteer klanten...',
    'Select department...': 'Selecteer een afdeling...',
    'Select division...': 'Selecteer een divisie...',
    'Select employer...': 'Selecteer een werkgever...',
    'Select Location': 'Locatie selecteren',
    'Select location first': 'Selecteer eerst een locatie',
    'Select Room': 'Selecteer kamer',
    'Single Day': 'Eenmalig',
    Skip: 'Overslaan',
    'Skip — no preference': 'Overslaan — geen voorkeur',
    Slot: 'Slot',
    'Smart Scheduling': 'Slimme planning',
    Start: 'Begin',
    'Start Date': 'Startdatum',
    'Start Over': 'Opnieuw beginnen',
    Submit: 'Verzenden',
    success: 'Succes!',
    'Tell us more': 'Toelichting',
    Text: 'Tekst',
    'This activity is location-restricted. It can only be booked at the location(s) shown below.':
      'Deze activiteit is aan een locatie gebonden. U kunt deze alleen boeken op de locatie(s) hieronder.',
    'This selected event': 'Dit geselecteerde evenement',
    'This selected event and following events':
      'Dit geselecteerde evenement en volgende evenementen',
    'This time slot is no longer available': 'Dit tijdslot is niet meer beschikbaar',
    Time: 'Tijd',
    'Time band completion': 'Tijdvakvulling',
    'Time Configuration': 'Tijdinstellingen',
    'Time preference': 'Tijdvoorkeur',
    to: 'tot',
    'To Date': 'Tot datum',
    'Too early': 'Te vroeg',
    'Too late': 'Te laat',
    Update: 'Bijwerken',
    'Update Availability': 'Beschikbaarheid bijwerken',
    'Webshop Activity': 'Webshopactiviteit',
    Week: 'Week',
    Weekdays: 'Werkdagen',
    Weekends: 'Weekenden',
    'Why are you cancelling?': 'Waarom annuleert u?',
    'Why are you rescheduling?': 'Waarom plant u de afspraak opnieuw in?',
    'Why this slot?': 'Waarom dit tijdslot?',
    'Wrong location': 'Verkeerde locatie',
    'Wrong professional': 'Verkeerde professional',
    '{hours}h': '{hours} u',
    '{hours}h {minutes}m': '{hours} u {minutes} min',
    '{minutes}m': '{minutes} min',
    '{type} booked: {date} at {time}': '{type} geboekt: {date} om {time}',
    '{type} · {date} at {time} with {professionals}':
      '{type} · {date} om {time} met {professionals}',

    // Documents, tasks, messaging and person widgets (#4998)
    'An error occurred in the document editor': 'Er is een fout opgetreden in de documenteditor',
    'Appointment Status': 'Afspraakstatus',
    'Are you sure you want to mark all tasks as no-show?':
      'Weet u zeker dat u alle taken als no-show wilt markeren?',
    'Awaiting signature': 'Wacht op handtekening',
    'Awaiting your signature': 'Wacht op uw handtekening',
    'Certificate executed': 'Certificaat uitgevoerd',
    'Certificate updated': 'Certificaat bijgewerkt',
    'Chain History': 'Verzuimketen',
    'Coming soon': 'Binnenkort beschikbaar',
    'Confirmed by employee on {date}': 'Bevestigd door de werknemer op {date}',
    'Confirmed {relative} · employee': 'Bevestigd {relative} · werknemer',
    Conversation: 'Conversatie',
    'Could not export .ics file': 'Het .ics-bestand kon niet worden geëxporteerd',
    'Could not export the agenda list': 'De agendalijst kon niet worden geëxporteerd',
    'Could not load the agenda list': 'De agendalijst kon niet worden geladen',
    'Could not print the agenda list': 'De agendalijst kon niet worden afgedrukt',
    'Could not submit the credit request': 'Het creditverzoek kon niet worden ingediend',
    'Document saved': 'Document opgeslagen',
    'Document updated': 'Document bijgewerkt',
    'Documents could not be exported. Please try again.':
      'De documenten konden niet worden geëxporteerd. Probeer het opnieuw.',
    'Documents could not be loaded. Please try again.':
      'De documenten konden niet worden geladen. Probeer het opnieuw.',
    'Documents have been updated': 'De documenten zijn bijgewerkt',
    'Download certificate': 'Certificaat downloaden',
    'Downloading...': 'Downloaden...',
    Editing: 'Bewerken',
    'Email not verified': 'E-mailadres niet geverifieerd',
    'Email verified {date}': 'E-mailadres geverifieerd op {date}',
    employee: 'werknemer',
    'Enter whole hours, like 32 or 36': 'Voer hele uren in, zoals 32 of 36',
    'Execute the certificate to download it.': 'Voer het certificaat uit om het te downloaden.',
    'Executing...': 'Uitvoeren...',
    'Exit Fullscreen': 'Volledig scherm sluiten',
    'Failed to execute the certificate': 'Het certificaat kon niet worden uitgevoerd',
    'Failed to load activity.': 'De activiteit kon niet worden geladen.',
    'Failed to load document editor': 'De documenteditor kon niet worden geladen',
    'Failed to load messages.': 'De berichten konden niet worden geladen.',
    'Failed to load task owners': 'De taakeigenaren konden niet worden geladen',
    'Failed to save document': 'Het document kon niet worden opgeslagen',
    'Failed to update task owner': 'De taakeigenaar kon niet worden bijgewerkt',
    'Failed to update the certificate': 'Het certificaat kon niet worden bijgewerkt',
    Fullscreen: 'Volledig scherm',
    'Fully signed': 'Volledig ondertekend',
    import: 'import',
    Join: 'Deelnemen',
    'Join Meeting': 'Deelnemen aan videogesprek',
    'Last changed via {source} on {date}': 'Laatst gewijzigd via {source} op {date}',
    List: 'Lijst',
    'Loading conversation…': 'Conversatie laden…',
    'Loading document editor...': 'Documenteditor laden...',
    Meeting: 'Videogesprek',
    'Meeting Link': 'Link videogesprek',
    'Message could not be sent. Please try again.':
      'Het bericht kon niet worden verzonden. Probeer het opnieuw.',
    'Never confirmed': 'Nooit bevestigd',
    'Never confirmed by employee': 'Nooit bevestigd door de werknemer',
    'No activity yet in this file.': 'Nog geen activiteit in dit dossier.',
    'No certificate available.': 'Geen certificaat beschikbaar.',
    'No conversations about this subject yet.': 'Nog geen conversaties over dit onderwerp.',
    'One or more tasks have already been executed, so this activity can no longer be rescheduled.':
      'Een of meer taken zijn al uitgevoerd, dus deze activiteit kan niet meer opnieuw worden ingepland.',
    Ongoing: 'Lopend',
    'Open conversation': 'Conversatie openen',
    'Open conversation: {topic}': 'Conversatie openen: {topic}',
    'Please enter a valid page number between 1 and {max}':
      'Voer een geldig paginanummer in tussen 1 en {max}',
    'Please review the updated documents and confirm your acceptance to continue using the portal.':
      'Bekijk de bijgewerkte documenten en bevestig dat u ze accepteert om het portaal te blijven gebruiken.',
    'Please save your changes before switching modes':
      'Sla uw wijzigingen op voordat u van weergave wisselt',
    'Please select a task owner': 'Selecteer een taakeigenaar',
    'Preparing…': 'Voorbereiden…',
    'Recent activity': 'Recente activiteit',
    'Recent messages': 'Recente berichten',
    Schedules: 'Planning',
    'Select a new task owner from the list below': 'Selecteer hieronder een nieuwe taakeigenaar',
    'Select task owner': 'Selecteer een taakeigenaar',
    'Signature could not be saved. Please check the highlighted fields.':
      'De handtekening kon niet worden opgeslagen. Controleer de gemarkeerde velden.',
    'Signature could not be saved. Please try again.':
      'De handtekening kon niet worden opgeslagen. Probeer het opnieuw.',
    'Signatures could not be loaded. Please try again.':
      'De handtekeningen konden niet worden geladen. Probeer het opnieuw.',
    Someone: 'Iemand',
    'Start one via the kebab menu → Messages.': 'Start een conversatie via het menu ⋮ → Berichten.',
    'Summary of Changes': 'Overzicht van de wijzigingen',
    system: 'systeem',
    'Task owner updated successfully': 'Taakeigenaar bijgewerkt',
    'The conversation moves out of your open list for every participant. You can find it again under archived conversations.':
      'De conversatie verdwijnt voor alle deelnemers uit de lijst met open conversaties. U vindt deze terug onder gearchiveerde conversaties.',
    'The message is removed for everyone in this conversation and replaced by a "message deleted" notice. This cannot be undone.':
      'Het bericht wordt voor iedereen in deze conversatie verwijderd en vervangen door de melding "bericht verwijderd". Dit kan niet ongedaan worden gemaakt.',
    'the subject': 'de betrokkene',
    'There are {count} pending action lists associated with this absence. Do you want to delete them as well?':
      'Er zijn {count} openstaande actielijsten gekoppeld aan dit verzuim. Wilt u die ook verwijderen?',
    'This activity can no longer be rescheduled.':
      'Deze activiteit kan niet meer opnieuw worden ingepland.',
    'This activity cannot be concluded right now':
      'Deze activiteit kan op dit moment niet worden afgerond',
    'This activity has been invoiced and can no longer be rescheduled.':
      'Deze activiteit is gefactureerd en kan niet meer opnieuw worden ingepland.',
    'This activity is concluded and can no longer be rescheduled.':
      'Deze activiteit is afgerond en kan niet meer opnieuw worden ingepland.',
    'This activity is on hold and can no longer be rescheduled.':
      'Deze activiteit is gepauzeerd en kan niet meer opnieuw worden ingepland.',
    'This permanently removes the document and all of its versions for everyone with access. This action cannot be undone.':
      'Hiermee verwijdert u het document en alle versies ervan definitief voor iedereen met toegang. Dit kan niet ongedaan worden gemaakt.',
    'This portal is technically provided by InCTRL, acting as a data processor on behalf of {organization}.':
      'Dit portaal wordt technisch geleverd door InCTRL, handelend als verwerker namens {organization}.',
    'Updating regenerates the certificate with the latest data. Continue?':
      'Bijwerken genereert het certificaat opnieuw met de nieuwste gegevens. Doorgaan?',
    'Updating...': 'Bijwerken...',
    User: 'Gebruiker',
    version: 'versie | versies',
    'View activity': 'Activiteit bekijken',
    'View all': 'Alles bekijken',
    'View Preview': 'Voorbeeld bekijken',
    yesterday: 'gisteren',
    'You have unsaved changes': 'U heeft niet-opgeslagen wijzigingen',

    // Backtick keys in PatientMeetingView, found by the coverage spec (#4999)
    'Camera disabled.': 'Camera uitgeschakeld.',
    'Input devices disabled.': 'Invoerapparaten uitgeschakeld.',
    'Microphone disabled.': 'Microfoon uitgeschakeld.',

    // Text that bypassed vue-i18n: toasts, glued sentences, labels, statuses (#5000)
    '"{name}" is larger than 10 MB.': '"{name}" is groter dan 10 MB.',
    '{n} person viewing now | {n} people viewing now':
      '{n} persoon kijkt nu mee | {n} personen kijken nu mee',
    'Cannot move: outside professional availability.':
      'Verplaatsen niet mogelijk: buiten de beschikbaarheid van de professional.',
    'Cannot resize: outside professional availability.':
      'Duur wijzigen niet mogelijk: buiten de beschikbaarheid van de professional.',
    'Civil Partnership': 'Geregistreerd partnerschap',
    Cohabiting: 'Samenwonend',
    Confirmed: 'Bevestigd',
    'Could not load the diagnosis codes for this chapter.':
      'De diagnosecodes voor dit hoofdstuk konden niet worden geladen.',
    Divorced: 'Gescheiden',
    'Document restored to version {version}.': 'Document hersteld naar versie {version}.',
    'Duration only — price unchanged': 'Alleen de duur gewijzigd — prijs ongewijzigd',
    'Employer Portal': 'Werkgeversportaal',
    Engaged: 'Verloofd',
    'Event moved.': 'Afspraak verplaatst.',
    'Event moved. Click to undo.': 'Afspraak verplaatst. Klik om ongedaan te maken.',
    'Event reverted.': 'Verplaatsing ongedaan gemaakt.',
    'Exporting the data failed. Please try again.':
      'Het exporteren van de gegevens is mislukt. Probeer het opnieuw.',
    'Failed to delete employment': 'Het dienstverband kon niet worden verwijderd',
    'Failed to load circumstances options.':
      'De opties voor omstandigheden konden niet worden geladen.',
    'Failed to load person information.': 'De persoonsgegevens konden niet worden geladen.',
    'Failed to load ticket details.': 'De ticketgegevens konden niet worden geladen.',
    'Failed to move event.': 'De afspraak kon niet worden verplaatst.',
    'Failed to reset event colours': 'De afspraakkleuren konden niet worden hersteld',
    'Failed to resize event.': 'De duur van de afspraak kon niet worden gewijzigd.',
    'Failed to undo move.': 'De verplaatsing kon niet ongedaan worden gemaakt.',
    'Failed to update calendar events': 'De agenda-afspraken konden niet worden bijgewerkt',
    'Failed to update circumstances.': 'De omstandigheden konden niet worden bijgewerkt.',
    'Failed to update event colours': 'De afspraakkleuren konden niet worden bijgewerkt',
    'Failed to update heatmap preference': 'De heatmapvoorkeur kon niet worden bijgewerkt',
    'Failed to update UWV information. Please try again.':
      'De UWV-gegevens konden niet worden bijgewerkt. Probeer het opnieuw.',
    'Hours cannot be greater than the employer hours ({hours}).':
      'Het aantal uren kan niet hoger zijn dan de uren bij de werkgever ({hours}).',
    'In a Relationship': 'In een relatie',
    Initial: 'Eerste versie',
    'Invalid document format. Allowed formats: {formats}':
      'Ongeldig documentformaat. Toegestane formaten: {formats}',
    'Invalid signature format. Allowed formats: {formats}':
      'Ongeldig handtekeningformaat. Toegestane formaten: {formats}',
    Married: 'Gehuwd',
    'Person Portal': 'Persoonsportaal',
    'Planned only — final price set on execution':
      'Alleen gepland — de definitieve prijs wordt bij uitvoering bepaald',
    'Prefer Not To Say': 'Zeg ik liever niet',
    'Recurring events moved.': 'Terugkerende afspraken verplaatst.',
    'Reply sent.': 'Antwoord verzonden.',
    'Saving the diagnosis failed.': 'Het opslaan van de diagnose is mislukt.',
    Separated: 'Gescheiden levend',
    'Server is not responding or please check your internet connection.':
      'De server reageert niet. Controleer uw internetverbinding.',
    'Single (relationship status)': 'Alleenstaand',
    'Support service authentication failed. Please try again later.':
      'Aanmelden bij de supportdienst is mislukt. Probeer het later opnieuw.',
    'The requested item could not be found.': 'Het gevraagde item is niet gevonden.',
    'The saved task data could not be read.':
      'De opgeslagen taakgegevens konden niet worden gelezen.',
    'the organization': 'de organisatie',
    'Ticket server is not responding. Please try again later.':
      'De ticketserver reageert niet. Probeer het later opnieuw.',
    'Too many attempts. Please wait a moment and try again.':
      'Te veel pogingen. Wacht even en probeer het opnieuw.',
    Widowed: 'Weduwe of weduwnaar',
    'You can attach up to {max} files.': 'U kunt maximaal {max} bestanden bijvoegen.',
    'You can only select up to {max} professionals at a time.':
      'U kunt maximaal {max} professionals tegelijk selecteren.',
    'You do not have permission to do this.': 'U heeft geen toestemming om dit te doen.',
    'You have been signed out in another tab.': 'U bent in een ander tabblad afgemeld.',
    'Your account has been frozen. Please contact an administrator.':
      'Uw account is bevroren. Neem contact op met een beheerder.',
    'Your session has expired. Please sign in again.': 'Uw sessie is verlopen. Meld u opnieuw aan.',

    // Sentence case over EMR's Title Case (see PROFESSIONAL_WORDING_KEYS) and assistive text (#5002)
    'Absence Date - From': 'Verzuimdatum - van',
    'Basic Information': 'Basisinformatie',
    'Confirm & Start': 'Bevestigen & starten',
    'Document viewer': 'Documentweergave',
    'Due Date - From': 'Vervaldatum - van',
    'Invoiced Activities': 'Gefactureerde activiteiten',
    'JPG or PNG, max. 5 MB': 'JPG of PNG, max. 5 MB',
    'My Calendar': 'Mijn agenda',
    'PDF or DOCX, max. 5 MB': 'PDF of DOCX, max. 5 MB',
    'Platform Information': 'Platforminformatie',
    'Privacy & Legal': 'Privacy & juridisch',
    'Privacy & Legal Information': 'Privacy & juridische informatie',
    'Privacy Policy Document': 'Document privacybeleid',
    'Profile picture': 'Profielfoto',
    'QR code': 'QR-code',
    'Roles & Qualifications': 'Rollen & kwalificaties',
    'Security & Compliance': 'Beveiliging & compliance',
    'Select Date Range': 'Datumbereik selecteren',
    'Select Person': 'Persoon selecteren',
    'Status & Timeline': 'Status & tijdlijn',
    'Terms & Conditions Document': 'Document Algemene Voorwaarden',

    // Formal values for keys EMR's shared nl.json says with "je"; see PROFESSIONAL_FORMAL_KEYS (#5001)
    'Calendar token regenerated. Update the subscription URL in your calendar app.':
      'Agendatoken opnieuw gegenereerd. Werk de abonnements-URL bij in uw agenda-app.',
    'Subscribe to your agenda in Apple Calendar, Google Calendar or Outlook. Changes appear when your calendar app refreshes: Apple Calendar lets you choose how often, Google Calendar and Outlook can take up to 24 hours.':
      'Abonneer u op uw agenda in Apple Calendar, Google Calendar of Outlook. Wijzigingen verschijnen zodra uw agenda-app ververst: in Apple Calendar kiest u zelf hoe vaak, bij Google Calendar en Outlook kan het tot 24 uur duren.',
    'Use your mouse, touchpad, or finger to draw your signature above':
      'Gebruik uw muis, touchpad of vinger om hierboven uw handtekening te tekenen',
    'You cannot cancel all tasks because there are completed tasks':
      'U kunt niet alle taken annuleren omdat er afgeronde taken zijn',
    'You cannot conclude this action because there are pending tasks':
      'U kunt deze actie niet afronden omdat er nog openstaande taken zijn',
    'You cannot conclude this activity because there are still tasks that are pending.':
      'U kunt deze activiteit niet afronden omdat er nog taken openstaan.',
    'You cannot no show all tasks because there are completed tasks':
      'U kunt niet alle taken als no-show markeren omdat er afgeronde taken zijn',
    'You do not have permission to add activities':
      'U heeft geen toestemming om activiteiten toe te voegen',
    'You do not have permission to cancel all the tasks':
      'U heeft geen toestemming om alle taken te annuleren',
    'You do not have permission to conclude this action':
      'U heeft geen toestemming om deze actie af te ronden',
    'You do not have permission to mark no show all the tasks':
      'U heeft geen toestemming om alle taken als no-show te markeren',
    'You do not have permission to view this page':
      'U heeft geen toestemming om deze pagina te bekijken',
    'You have successfully logged in': 'U bent succesvol ingelogd',
    'You need to create a signature first before you can sign documents.':
      'U moet eerst een handtekening aanmaken voordat u documenten kunt ondertekenen.',
    'You will not be able to recover this appointment!': 'U kunt deze afspraak niet herstellen!',
    "You've been inactive for a while. Your session will expire soon.":
      'U bent een tijdje niet actief geweest. Uw sessie verloopt binnenkort.',
    'Your appointment has been deleted.': 'De afspraak is verwijderd.',
    'Your current subscription URL will stop working immediately. Every calendar app that uses it has to be subscribed again with the new URL.':
      'Uw huidige abonnements-URL werkt direct niet meer. Elke agenda-app die deze gebruikt, moet opnieuw worden geabonneerd met de nieuwe URL.',
    'No longer available': 'Niet meer beschikbaar',
    'No access': 'Geen toegang',
    'Could not load': 'Kon niet worden geladen',
    'This absence no longer exists — it may have been deleted.':
      'Dit verzuim bestaat niet meer — het is mogelijk verwijderd.',
    'This employment no longer exists — it may have been deleted.':
      'Dit dienstverband bestaat niet meer — het is mogelijk verwijderd.',
    'This document no longer exists — it may have been deleted.':
      'Dit document bestaat niet meer — het is mogelijk verwijderd.',
    'This task no longer exists — it may have been deleted.':
      'Deze taak bestaat niet meer — deze is mogelijk verwijderd.',
    'You are not authorised to view this absence.': 'U bent niet bevoegd dit verzuim te bekijken.',
    'You are not authorised to view this employment.':
      'U bent niet bevoegd dit dienstverband te bekijken.',
    'You are not authorised to view this document.':
      'U bent niet bevoegd dit document te bekijken.',
    'You are not authorised to view this task.': 'U bent niet bevoegd deze taak te bekijken.',
    'This person no longer exists — they may have been deleted.':
      'Deze persoon bestaat niet meer — deze is mogelijk verwijderd.',
    'You are not authorised to view this person.': 'U bent niet bevoegd deze persoon te bekijken.',
  },
}

Object.assign(LOCAL_MESSAGES.en, AUTH_MESSAGES.en)
Object.assign(LOCAL_MESSAGES.nl, AUTH_MESSAGES.nl)

// `?lang=en|nl` on an invitation mail's buttons (EMR#5030).
function readUrlLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null
  const lang = new URLSearchParams(window.location.search).get('lang')
  return isSupportedLocale(lang) ? lang : null
}

/**
 * nl keys whose bundled value wins over EMR's shared nl.json, the mirror of the patient portal's
 * PATIENT_INFORMAL_OVERRIDE_KEYS. EMR's store says "je" for keys the person portal also reads; this
 * portal says "u" (#5001). A key leaves the list once EMR's value is formal: the spec checks it against
 * the snapshot that `bun run i18n:refresh-emr` writes.
 */
export const PROFESSIONAL_FORMAL_KEYS: readonly string[] = [
  'Calendar token regenerated. Update the subscription URL in your calendar app.',
  'Subscribe to your agenda in Apple Calendar, Google Calendar or Outlook. Changes appear when your calendar app refreshes: Apple Calendar lets you choose how often, Google Calendar and Outlook can take up to 24 hours.',
  'Use your mouse, touchpad, or finger to draw your signature above',
  'You cannot cancel all tasks because there are completed tasks',
  'You cannot conclude this action because there are pending tasks',
  'You cannot conclude this activity because there are still tasks that are pending.',
  'You cannot no show all tasks because there are completed tasks',
  'You do not have permission to add activities',
  'You do not have permission to cancel all the tasks',
  'You do not have permission to conclude this action',
  'You do not have permission to mark no show all the tasks',
  'You do not have permission to view this page',
  'You have successfully logged in',
  'You need to create a signature first before you can sign documents.',
  'You will not be able to recover this appointment!',
  "You've been inactive for a while. Your session will expire soon.",
  'Your appointment has been deleted.',
  'Your current subscription URL will stop working immediately. Every calendar app that uses it has to be subscribed again with the new URL.',
]

/**
 * Keys whose bundled wording is deliberate: lowercase mid-sentence units, "2 u" (#5001) and Dutch
 * sentence case over EMR's English-style Title Case (#5002). Re-applied for en too where the bundled
 * en has the key, because EMR's en.json capitalises "Hour", "Minutes" and "New" mid-sentence.
 */
export const PROFESSIONAL_WORDING_KEYS: readonly string[] = [
  'hour',
  'minutes',
  'new',
  '{hours}h',
  'Yearly Absence Analysis',
  'Thanks for completing the form!',
  'Thanks for updating the form!',
  'Absence Date - From',
  'Basic Information',
  'Confirm & Start',
  'Due Date - From',
  'Invoiced Activities',
  'My Calendar',
  'Platform Information',
  'Privacy & Legal',
  'Privacy & Legal Information',
  'Privacy Policy Document',
  'Roles & Qualifications',
  'Schedule Appointment',
  'Security & Compliance',
  'Select Date Range',
  'Select Location',
  'Select Person',
  'Status & Timeline',
  'Terms & Conditions Document',
]

/** EMR's messages win over the bundled ones, except the keys listed above. */
export function mergeServerMessages(
  locale: string,
  server: Record<string, string>,
): Record<string, string> {
  const bundled = LOCAL_MESSAGES[locale as SupportedLocale] ?? {}
  const merged = { ...bundled, ...server }
  const keep =
    locale === 'nl'
      ? [...PROFESSIONAL_FORMAL_KEYS, ...PROFESSIONAL_WORDING_KEYS]
      : PROFESSIONAL_WORDING_KEYS.filter((key) => key in bundled)
  for (const key of keep) merged[key] = bundled[key]
  return merged
}

/**
 * Detect browser language and map to supported locale.
 * Priority: ?lang > localStorage > browser language > default (nl)
 *
 * Runs at boot, before any user is known. `AuthStore.setLocale()` then layers
 * the profile locale on top, but only when the profile has a supported one and
 * no explicit pick was made, so the effective post-login order is:
 * ?lang or toggle > profile (when set) > localStorage > browser language > default (nl)
 */
function detectLocale(): SupportedLocale {
  const urlLocale = readUrlLocale()
  if (urlLocale) return urlLocale

  const savedLocale = localStorage.getItem('locale')
  if (isSupportedLocale(savedLocale)) {
    return savedLocale
  }

  if (typeof navigator !== 'undefined') {
    const browserLang =
      navigator.language || (navigator as unknown as Record<string, string>).userLanguage
    if (browserLang) {
      const langCode = browserLang.split('-')[0].toLowerCase()
      if (isSupportedLocale(langCode)) {
        return langCode
      }
    }
  }

  return DEFAULT_LOCALE
}

function applyDocumentLang(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('lang', locale)
  // index.html's static title is English (#5002). The locale is passed explicitly: init and
  // setLocale call this before the instance switches to it.
  const global = i18nInstance?.global as unknown as
    | { t: (key: string, named: object, options: { locale: string }) => string }
    | undefined
  if (global) document.title = `${APP_NAME} - ${global.t('Professional Portal', {}, { locale })}`
}

/**
 * Set locale and persist to localStorage
 */
export function setLocale(locale: SupportedLocale): void {
  if (!i18nInstance) {
    console.warn('i18n instance not available')
    return
  }

  localStorage.setItem('locale', locale)
  applyDocumentLang(locale)
  setDatetimeLocale(locale)
  const localeObj = i18nInstance.global.locale as unknown as Record<string, string> | string
  if (typeof localeObj === 'object' && localeObj !== null && 'value' in localeObj) {
    ;(localeObj as { value: string }).value = locale
  } else {
    ;(i18nInstance.global as unknown as Record<string, unknown>).locale = locale
  }
}

/**
 * Tracks whether the user made an explicit locale choice before authenticating
 * (the login-page toggle). When set, AuthStore must NOT overwrite it with the
 * stored profile locale after login — otherwise a user whose profile locale
 * differs from their login-page pick sees the language flip on login (#838).
 * Scoped to sessionStorage so it governs the current login only and is cleared
 * on logout.
 */
const EXPLICIT_LOCALE_KEY = 'locale_explicit'

export function markExplicitLocaleSelection(): void {
  try {
    sessionStorage.setItem(EXPLICIT_LOCALE_KEY, '1')
  } catch {
    // sessionStorage unavailable (private mode / SSR) — degrade silently.
  }
}

export function hasExplicitLocaleSelection(): boolean {
  try {
    return sessionStorage.getItem(EXPLICIT_LOCALE_KEY) === '1'
  } catch {
    return false
  }
}

export function clearExplicitLocaleSelection(): void {
  try {
    sessionStorage.removeItem(EXPLICIT_LOCALE_KEY)
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

/**
 * Get current locale
 */
export function getLocale(): SupportedLocale {
  if (i18nInstance) {
    const locale = i18nInstance.global.locale
    const localeValue =
      typeof locale === 'object' && locale !== null && 'value' in locale ? locale.value : locale
    return localeValue as SupportedLocale
  }
  return detectLocale()
}

/**
 * Get i18n instance
 */
export function getI18nInstance(): I18n | null {
  return i18nInstance
}

/**
 * Fetch translations from API
 */
async function fetchTranslations(locale: string) {
  try {
    console.log(`Fetching translations for locale: ${locale}`)
    const response = await fetch(`${API_URL}/locale/${locale}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const translations = await response.json()
    console.log(`Successfully fetched translations for ${locale}:`, translations)
    return translations
  } catch (error) {
    console.warn(`Error fetching translations for ${locale}:`, error)
    console.log(`Using fallback translations for ${locale}`)

    const fallbackTranslations = {
      en: {
        Save: 'Save',
        Saved: 'Saved',
        'Oops! The page you are looking for does not exist.':
          'Oops! The page you are looking for does not exist.',
        'Go to Home': 'Go to Home',
        success: 'Success',
      },
      nl: {
        Save: 'Opslaan',
        Saved: 'Opgeslagen',
        'Oops! The page you are looking for does not exist.':
          'Oeps! De pagina die u zoekt bestaat niet.',
        'Go to Home': 'Ga naar Home',
        success: 'Succes',
      },
    }
    return fallbackTranslations[locale as keyof typeof fallbackTranslations] || {}
  }
}

/**
 * Update translations for a specific locale
 */
export async function updateTranslations(locale: string) {
  if (!i18nInstance) {
    console.warn('i18n instance not available for updating translations')
    return
  }

  try {
    const translations = await fetchTranslations(locale)
    i18nInstance.global.setLocaleMessage(locale, mergeServerMessages(locale, translations))
    console.log(`Translations updated for locale: ${locale}`)
  } catch (error) {
    console.error(`Failed to update translations for ${locale}:`, error)
  }
}

/**
 * Refresh all translations
 */
export async function refreshAllTranslations() {
  if (!i18nInstance) {
    console.warn('i18n instance not available for refreshing translations')
    return
  }

  try {
    const [enTranslations, nlTranslations] = await Promise.all([
      fetchTranslations('en'),
      fetchTranslations('nl'),
    ])

    i18nInstance.global.setLocaleMessage('en', mergeServerMessages('en', enTranslations))
    i18nInstance.global.setLocaleMessage('nl', mergeServerMessages('nl', nlTranslations))
    console.log('All translations refreshed successfully')
  } catch (error) {
    console.error('Failed to refresh translations:', error)
  }
}

/**
 * Initialize i18n with fetched translations
 */
async function initializeI18n() {
  console.log('Initializing i18n with API translations...')

  try {
    const [enTranslations, nlTranslations] = await Promise.all([
      fetchTranslations('en'),
      fetchTranslations('nl'),
    ])

    const detectedLocale = detectLocale()
    if (!localStorage.getItem('locale')) {
      localStorage.setItem('locale', detectedLocale)
    }

    i18nInstance = createI18n({
      locale: detectedLocale,
      fallbackLocale: 'en',
      messages: {
        en: mergeServerMessages('en', enTranslations),
        nl: mergeServerMessages('nl', nlTranslations),
      },
      legacy: false,
      // `t('Available in {minutes} min', { minutes })` renders "Available in
      // 30 min" instead of the raw "{minutes}" placeholder. Without this, a
      fallbackFormat: true,
      missingWarn: false,
      fallbackWarn: false,
    })

    applyDocumentLang(detectedLocale)
    setDatetimeLocale(detectedLocale)
    const global = i18nInstance.global as unknown as { t: (key: string) => string }
    setTranslator((key) => global.t(key))

    // A `?lang` link counts as a pick on the LanguageToggle: persisted, and kept after login.
    const urlLocale = readUrlLocale()
    if (urlLocale) {
      setLocale(urlLocale)
      markExplicitLocaleSelection()
    }

    console.log('i18n initialized successfully')
    return i18nInstance
  } catch (error) {
    console.error('Failed to initialize i18n:', error)
    throw error
  }
}

export default initializeI18n()
